import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { toast } from 'react-toastify';
import { FaCheck, FaCheckDouble, FaChevronDown, FaChevronRight, FaExclamationTriangle, FaEye, FaFilePdf, FaInfoCircle, FaLink, FaLock, FaPen, FaPencilAlt, FaPlaneArrival, FaPlaneDeparture, FaPlus, FaRedo, FaTicketAlt, FaTimes, FaTrash, FaUndo } from 'react-icons/fa';
import ConfirmarAcaoModal from './ConfirmarAcaoModal';
import DadosParticipante from './DadosParticipante';
import { FinCheck, FinField, FinInput, FinNumber, FinTextarea } from './FinField';
import { IconeInfo, ObservacaoEditavel, PopoverObservacao } from './PillObservacao';
import ReactSelect from './ReactSelect';
import { moeda, nomeDoArquivo } from '../util/util';

/**
 * A planilha de emissão de aéreo na tela: uma linha por ENTRADA do participante (a anterior fica
 * visível logo acima da atual, com a requisição, os bilhetes e os valores dela), agrupadas pela
 * requisição em que ele entrou pela primeira vez, com uma faixa antes de cada grupo — a mesma ordem
 * e o mesmo Nº da planilha exportada, que vêm do back (`ordemDaPlanilha`). No layout da planilha que
 * o Aéreo usa (legenda vem do back, `EventoAereoEmissao::LEGENDA`). É a MESMA planilha nas duas
 * telas — Solicitar Emissão de Aéreo (RSVP, só leitura) e Emissão de Aéreo (Aéreo, que escolhe o
 * status e escreve a observação e grava os bilhetes). Uma tabela só, para as duas mudarem juntas.
 *
 * Ninguém digita valor: tarifa, taxas e FEE são lidos do PDF do bilhete, e a linha mostra um
 * valor só (total com FEE); a planilha inteira sai pela exportação. O status e a observação que
 * o Aéreo muda ficam em RASCUNHO na tela até o Salvar ou o "Enviar para o RSVP" do rodapé. O Salvar de
 * cada lado guarda no rascunho dele, e o back já manda cada linha com o rascunho do lado da tela por cima
 * (`aereoPendente` e `rsvpPendente` dizem que ele existe): o outro lado só vê no envio.
 */

// Baixa um blob da API. Erro também vem em blob: desempacota o JSON para mostrar a mensagem real.
// `corpo`: POST com ele; sem ele, GET.
export async function baixarBlob(url, token, nomePadrao, corpo = null) {
    try {
        const config = { headers: { Authorization: `Bearer ${token}` }, responseType: 'blob' };
        const res = await (corpo ? axios.post(url, corpo, config) : axios.get(url, config));
        const nome = nomeDoArquivo(res.headers?.['content-disposition'], nomePadrao);
        const link = document.createElement('a');
        link.href = window.URL.createObjectURL(res.data);
        link.download = nome;
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(link.href);
        return true;
    } catch (err) {
        let msg = 'Não foi possível baixar.';
        try {
            const texto = await err?.response?.data?.text?.();
            const json = texto ? JSON.parse(texto) : null;
            msg = json?.error || json?.msg || msg;
        } catch (e) { /* mensagem padrão */ }
        toast.error(msg);
        return false;
    }
}

export const TRECHOS_EMISSAO = [['ida', 'Ida'], ['retorno', 'Retorno'], ['ida_volta', 'Ida e volta']];
const ROTULO_TRECHO = Object.fromEntries(TRECHOS_EMISSAO);

// Status que não pedem observação: o inicial e o que só a gravação do bilhete marca.
export const ROTULOS_SEM_OBSERVACAO = ['EMITIR', 'EMITIDO'];

// Os status de cancelamento: saem do select e vão pelo botão Cancelar da linha, com descrição
// obrigatória, nas duas telas. Fora isso são status como os outros: rascunho até o envio.
export const ehCancelamento = (rotulo) => String(rotulo ?? '').startsWith('CANCELADO');
// Cancelamento com reembolso: o Cancelar do Aéreo pede o reembolso previsto, e a linha o mostra no Valor.
const ehReembolso = (rotulo) => ehCancelamento(rotulo) && String(rotulo).includes('REEMBOLSO');
// Pseudo-status da tela do RSVP: participante que ainda não entrou em nenhuma requisição.
export const ROTULO_NAO_SOLICITADO = 'NÃO SOLICITADO';
// Pseudo-status das duas telas: a marca de cancelamento do RSVP. Não é status — a linha fica com o
// que o Aéreo deu —, mas é o que cada lado procura: o RSVP, o que pediu para cancelar; o Aéreo, o
// que tem de cancelar.
export const ROTULO_CANCELAMENTO_SOLICITADO = 'CANCELAMENTO DE EMISSÃO SOLICITADO';
// A cor dos pseudo-status, no formato da legenda (fundo da célula): o cancelamento pedido no vermelho
// do badge dele. O não solicitado fica no cinza.
const CORES_PSEUDO = { [ROTULO_CANCELAMENTO_SOLICITADO]: ['C1232B', 'FFFFFF', true] };

// Os três status que a planilha divide para chegar ao TKT médio (a fórmula do rodapé dela).
const ROTULOS_DO_TKT_MEDIO = ['PENDENTE APROVAÇÃO CUSTOS', 'EMITIDO', 'CANCELADO /ALTERAÇÃO IDA'];

const SITUACAO_BILHETE = {
    nao_lido:  ['Não lido', 'is-danger'],
    // Aviso, não trava: o Aéreo grava assim mesmo, se quiser.
    duplicado: ['Duplicado', 'is-warn'],
    pendente:  ['Pendente', 'is-warn'],
    gravado:   ['Gravado', 'is-ok'],
};

/**
 * O bilhete que o próximo envio do Aéreo grava: lido e casado (pendente, ou duplicado — aviso que
 * não trava), com participante e trecho, numa linha que não está cancelada na tela (`cancelada`,
 * `canceladaNaTela`): a cancelada recebe bilhete, mas ele só grava quando o Aéreo a marca como Emitido;
 * a emitida grava o novo também. A mesma regra do back (`bilhetesParaGravar`). A planilha e a página usam
 * ESTA função: quando cada uma tinha a sua, a da planilha olhava um campo que só a página montava, e o
 * Emitido nunca se habilitava. Na linha anterior, só o que ainda vale na entrega (`naEntrega`): o lado
 * que uma entrada posterior cancelou seria gravado no cadastro sem contar. O da linha com o voo trocado
 * depois da requisição (`aguardaRsvp`) fica até o RSVP pedir de novo (`semOsRetidos` no back).
 */
export const prontoParaGravar = (b, cancelada) =>
    ['pendente', 'duplicado'].includes(b?.situacao) && !!b.emissao && !!b.trecho && (!!b.atual || !!b.naEntrega)
    && !cancelada && !b.aguardaRsvp;
// O bilhete que estaria pronto, mas é da linha com o voo trocado: o Enviar não o leva, e a tela diz quem ficou.
export const retidoPeloVooTrocado = (b, cancelada) => !!b?.aguardaRsvp && prontoParaGravar({ ...b, aguardaRsvp: false }, cancelada);
// A linha está cancelada na tela: o status do rascunho do Aéreo é um cancelamento, ou, sem escolha nele, o
// gravado é cancelado. A marcada como Emitido no rascunho deixa de estar.
export const canceladaNaTela = (x, rascunhos) => {
    const r = x && rascunhos?.[x.id];
    return r && 'rotulo' in r && r.rotulo ? ehCancelamento(r.rotulo) : x?.situacao === 'cancelado';
};

export const brl = (v) => (v === null || v === undefined || v === '' ? '—' : `R$ ${moeda(Number(v))}`);

// Só o número de 13 dígitos se formata; o resto sai como está — o LOC que o bilhete da Azul, sem número,
// leva no e-ticket perderia as letras.
export const eticketFormatado = (v) => {
    const d = String(v ?? '').replace(/\D/g, '');
    return d.length === 13 ? `${d.slice(0, 3)} ${d.slice(3)}` : String(v ?? '').trim();
};

export const dataHora = (v) => {
    if (!v) return '';
    const m = String(v).match(/^(\d{4})-(\d{2})-(\d{2})(?:[ T](\d{2}):(\d{2}))?/);
    return m ? `${m[3]}/${m[2]}/${m[1]}${m[4] ? ` ${m[4]}:${m[5]}` : ''}` : String(v);
};

// As entradas do participante, uma por linha da planilha: as anteriores e, por último, a atual.
export const entradasDe = (l) => [...(l.anteriores ?? []), ...(l.emissao ? [l.emissao] : [])];

// Os bilhetes gravados que outro, gravado depois na mesma linha, substituiu: cada trecho que ele cobre tem um
// gravado mais novo. O cadastro guarda o último gravado de cada lado, e no mesmo Enviar a gravação segue a
// ordem de subida. O pendente não substitui nem é substituído: até o Enviar, o gravado é o que está no cadastro.
const LADOS_DO_TRECHO = { ida: ['ida'], retorno: ['retorno'], ida_volta: ['ida', 'retorno'] };
const ordemDaGravacao = (b) => `${b.emitidoEm ?? ''}|${b.criadoEm ?? ''}`;
const bilhetesSubstituidos = (bilhetes) => {
    const gravados = (bilhetes ?? []).filter(b => b.situacao === 'gravado' && LADOS_DO_TRECHO[b.trecho]);
    return new Set(gravados.filter(b => LADOS_DO_TRECHO[b.trecho].every(lado => gravados.some(x => x !== b
        && LADOS_DO_TRECHO[x.trecho].includes(lado) && ordemDaGravacao(x) > ordemDaGravacao(b)))).map(b => b.id));
};

// As anotações das observações — `observacao` do Aéreo, `observacaoRsvp` do RSVP —: lista
// { texto, por, nome, em, lado }, a mais antiga primeiro (`EventoAereoEmissao::anotar` no back). As do
// rascunho do lado da tela vêm com `rascunho` e a posição delas nele (`indice`), que o excluir pede; a
// última, quando é de quem está na tela, vem sem data: ela sai com a data do envio.
export const anotacoesDe = (v) => (Array.isArray(v) ? v : []);
export const textoMaisNovo = (v) => {
    const l = anotacoesDe(v);
    return l.length ? String(l[l.length - 1].texto ?? '') : '';
};

// As anotações dos dois lados juntas, da mais nova para a mais antiga, cada uma assinada com o lado, o
// nome e a data: o "22jul.aps:" que a operação escrevia à mão, sem o prefixo no texto. A célula mostra a
// mais nova de cada lado, uma linha cada, com a assinatura embaixo, e um "+n" com quantas ficaram de fora;
// o clique abre todas, inteiras. A que quem está na
// tela escreveu e ainda não gravou (`pendente`) entra no topo, marcada como não enviada, sem tirar
// nenhuma: anotação gravada não se substitui. A do rascunho do lado da tela ganha o excluir (`onExcluir`),
// que a enviada não tem.
// A Comunicação Intermediária (`comunicacao`) vem antes, numa linha, assinada embaixo como a anotação,
// só com o nome da coluna: é a coluna da planilha que o Aéreo escreve no lápis, sem autor nem data.
const semData = (a) => a.naoEnviada || (a.rascunho && !a.em);
const assinatura = (a) => [a.lado, a.nome, semData(a) ? 'não enviada' : dataHora(a.em).slice(0, 16).replace(/^(\d{2}\/\d{2})\/\d{4}/, '$1')]
    .filter(Boolean).join(' · ');
// A ordem das anotações: a data; a não enviada sem data é a mais nova, e a antiga sem data, a mais velha.
const ordemDaAnotacao = (a) => (semData(a) ? '9999' : String(a.em ?? ''));
const numaLinha = (v) => String(v ?? '').replace(/\s+/g, ' ').trim();
// A Comunicação Intermediária que a linha mostra: a do rascunho do lápis, quando há; senão, a gravada.
const comunicacaoNaTela = (e, rascunho) => String((rascunho && 'comunicacaoIntermediaria' in rascunho
    ? rascunho.comunicacaoIntermediaria : e?.comunicacaoIntermediaria) ?? '').trim();
function Observacoes({ rsvp, aereo, pendente, comunicacao, comunicacaoNaoEnviada, onExcluir = null, excluidas = [] }) {
    // As não enviadas que o X marcou ({ indice, texto }): saem no próximo Salvar, e até lá ficam riscadas. O X de
    // novo desmarca.
    const marcada = (a) => !!a.rascunho && excluidas.some(x => x.indice === a.indice && x.texto === a.texto);
    const riscada = (a) => (marcada(a) ? { textDecoration: 'line-through', color: 'var(--fin-mt)' } : undefined);
    const todas = [
        ...(pendente ? [pendente] : []),
        ...[...anotacoesDe(rsvp).map(a => ({ ...a, lado: 'RSVP' })), ...anotacoesDe(aereo).map(a => ({ ...a, lado: 'Aéreo' }))]
            .sort((a, b) => ordemDaAnotacao(b).localeCompare(ordemDaAnotacao(a))),
    ];
    const com = String(comunicacao ?? '').trim();
    if (!todas.length && !com) return null;
    // Na célula, depois da Comunicação Intermediária, a mais nova do RSVP e a do Aéreo (a não enviada é a mais
    // nova do lado dela), na ordem da lista.
    const naCelula = todas.filter(a => a === todas.find(x => x.lado === a.lado));
    const deFora = todas.length - naCelula.length;
    const rotulo = [
        todas.length > 1 ? `Observações (${todas.length})` : (todas.length ? 'Observação' : null),
        com ? 'Comunicação' : null,
    ].filter(Boolean).join(' / ');
    return (
        <PopoverObservacao rotulo={rotulo}
            titulo="Clique para ler as observações da linha inteiras, com quem e quando"
            conteudo={(
                <div className="d-flex flex-column gap-2">
                    {com && (
                        <div>
                            <div>{com}</div>
                            <div className="fin-pp__cell-muted" style={{ fontSize: 11, whiteSpace: 'normal' }}>
                                Comunicação Intermediária{comunicacaoNaoEnviada ? ' · não enviada' : ''}
                            </div>
                        </div>
                    )}
                    {todas.map((a, i) => (
                        <div key={i} className="d-flex align-items-start gap-2">
                            <div style={{ flex: 1, minWidth: 0 }}>
                                <div style={riscada(a)}>{a.texto}</div>
                                <div className="fin-pp__cell-muted" style={{ fontSize: 11, whiteSpace: 'normal' }}>
                                    {marcada(a) ? 'Sai no próximo Salvar' : assinatura(a)}
                                </div>
                            </div>
                            {/* Só a do rascunho de quem está na tela sai, e só no Salvar: a enviada fica. */}
                            {onExcluir && a.rascunho && (
                                <button type="button" className="fin-pp__btn fin-pp__btn--sm fin-pp__btn--danger-ghost"
                                    title={marcada(a) ? 'Desfazer: a observação fica' : 'Excluir esta observação, que ainda não foi enviada, no próximo Salvar'}
                                    aria-label={marcada(a) ? 'Desfazer a exclusão da observação' : 'Excluir a observação'}
                                    onClick={() => onExcluir(a)}>
                                    {marcada(a) ? <FaUndo size={10} /> : <FaTrash size={10} />}
                                </button>
                            )}
                        </div>
                    ))}
                </div>
            )}>
            {com && (
                <>
                    <span className="d-block text-truncate fin-pp__obs-linha fin-pp__obs-celula" style={{ fontSize: 12 }}>{numaLinha(com)}</span>
                    <span className="d-block fin-pp__cell-muted" style={{ fontSize: 11 }}>
                        Comunicação Intermediária{comunicacaoNaoEnviada ? ' · não enviada' : ''}
                    </span>
                </>
            )}
            {naCelula.map((a, i) => (
                <React.Fragment key={i}>
                    <span className="d-block text-truncate fin-pp__obs-linha fin-pp__obs-celula" style={{ fontSize: 12, marginTop: com || i ? 4 : 0, ...riscada(a) }}>
                        {numaLinha(a.texto)}
                    </span>
                    <span className="d-block fin-pp__cell-muted" style={{ fontSize: 11 }}>{assinatura(a)}</span>
                </React.Fragment>
            ))}
            {deFora > 0 && (
                <span className="d-block fin-pp__cell-muted" style={{ fontSize: 11, marginTop: 4, fontWeight: 700 }}
                    title="Clique para ler todas">+{deFora} {deFora === 1 ? 'observação' : 'observações'}</span>
            )}
        </PopoverObservacao>
    );
}
// A linha atual vai como emitida neste envio: os bilhetes prontos da entrega (os dela e os das
// anteriores que ainda valem), com os já gravados da emissão (`ladosEmitidos`), cobrem todo trecho que o
// participante usa — é a conta do back. Um lado só não emite, e a anterior não anuncia o Emitido: ele
// segue só da gravação. Só na tela do Aéreo, que é quem envia.
const vaiEmitirNaTela = (l, e, aereo, rascunhos = null) => {
    if (!aereo || !e || e.id !== l.emissao?.id || e.emitida) return false;
    const prontos = entradasDe(l).flatMap(x => (x.bilhetes ?? []).filter(b => prontoParaGravar(b, canceladaNaTela(x, rascunhos))));
    const cobre = (lado) => prontos.some(b => b.trecho === lado || b.trecho === 'ida_volta');
    return prontos.length > 0
        && (l.naoUtilizaIda || !!e.ladosEmitidos?.ida || cobre('ida'))
        && (l.naoUtilizaRetorno || !!e.ladosEmitidos?.retorno || cobre('retorno'));
};
// O pedido novo que a tela do RSVP tem para o participante, que entra na requisição em A EMITIR: a marca de
// quem abre linha nova — sem linha, ou com a atual cancelada pelo Aéreo —, gravada ou não. Sem seleção na
// tela (quem só lê), o pedido que o RSVP salvou (`pedidoSalvo`). `tela.marcados`: a seleção.
export const comPedidoNaTela = (l, tela = {}) => (tela.marcados
    ? tela.marcados.has(l.id) && (!l.emissao || l.emissao.situacao === 'cancelado')
    : !!l.pedidoSalvo);
// O status que a linha mostra: o escolhido no rascunho do Aéreo, inclusive na linha que emite neste
// envio; sem escolha, o Emitido que a gravação vai dar (`vaiEmitirNaTela`), também por cima do status que
// o Aéreo só salvou antes (`emitirPelaGravacao` no back); senão, o gravado. Quem ainda não tem entrada
// fica em "não solicitado" — na tela do RSVP, em A EMITIR quando está marcado (`comPedidoNaTela`).
// `tela`: { aereo, rascunhos, marcados }.
const rotuloNaTela = (l, e, tela = {}) => {
    const { aereo = false, rascunhos = null } = tela;
    const rascunho = (e && rascunhos?.[e.id]) || null;
    const escolhido = rascunho && 'rotulo' in rascunho ? rascunho.rotulo : null;
    if (escolhido !== null && escolhido !== undefined) return escolhido;
    if (vaiEmitirNaTela(l, e, aereo, rascunhos)) return 'EMITIDO';
    const ehAtual = !e || e.id === l.emissao?.id;
    return e?.rotulo?.[0] ?? (!aereo && ehAtual && comPedidoNaTela(l, tela) ? 'EMITIR' : ROTULO_NAO_SOLICITADO);
};

// O SLA da linha (`slaDaLinha` no back): { emissao, antecedencia }, cada um { dias, prazo, dentro } ou null.
const foraDoSla = (sla) => !!sla && ['emissao', 'antecedencia'].some(k => sla[k] && sla[k].dentro === false);
const textoDoSla = (sla) => [
    sla.emissao && `SLA emissão: ${sla.emissao.dias} ${sla.emissao.dias === 1 ? 'dia útil' : 'dias úteis'} (prazo ${sla.emissao.prazo})${sla.emissao.dentro ? '' : ', fora do prazo'}`,
    sla.antecedencia && `Antecedência do evento: ${sla.antecedencia.dias} ${sla.antecedencia.dias === 1 ? 'dia' : 'dias'} (prazo ${sla.antecedencia.prazo})${sla.antecedencia.dentro ? '' : ', fora do prazo'}`,
].filter(Boolean).join('\n');

// A reserva que o Aéreo informa no motivo da aprovação de custos, por lado (`CAMPOS_PREVISTOS` no back).
export const CAMPOS_PREVISTOS = { ida: ['tarifaPrevistaIda', 'taxasPrevistasIda'], retorno: ['tarifaPrevistaRetorno', 'taxasPrevistasRetorno'] };
const ROTULO_CUSTOS = 'PENDENTE APROVAÇÃO CUSTOS';
// Os lados que o status põe em aprovação de custos (`ladosEmAprovacaoDeCustos` no back): "X / Y" é ida / volta, e o
// status sem barra vale para os dois.
export const ladosEmCustos = (rotulo) => {
    const partes = String(rotulo ?? '').split(' / ');
    if (partes.length === 1) return partes[0] === ROTULO_CUSTOS ? ['ida', 'retorno'] : [];
    return ['ida', 'retorno'].filter((lado, i) => partes[i] === ROTULO_CUSTOS);
};
// Os valores da linha como a tela os mostra: os bilhetes (`totalDosBilhetes`) e a reserva prevista do lado em
// aprovação de custos que ainda não tem bilhete — a do rascunho da tela, quando ele tem uma; o back soma a gravada
// (`valoresDaEntrada`), e o lado que o status da tela tirou de custos a perde no Salvar (`regraDosPrevistos`).
// O FEE é o da Access, que o back já calcula (FEE por bilhete da linha, ou o padrão do cliente, × NUM TKT); com o FEE
// por bilhete no rascunho da tela (aba FEE do lápis), ele × NUM TKT, e o vazio volta ao padrão (`tela.feePadrao`).
// Devolve { total, fee, comFee, previstos: { ida, retorno } } (null no lado sem reserva). Sem valor nenhum, total null.
export const valorNaTela = (l, e, tela = {}) => {
    const r = tela.rascunhos?.[e.id];
    const emCustos = ladosEmCustos(rotuloNaTela(l, e, tela));
    const previstos = { ida: null, retorno: null };
    // Sem `totalDosBilhetes` (resposta de antes da reserva prevista), o total que veio.
    let total = e.valores && 'totalDosBilhetes' in e.valores ? e.valores.totalDosBilhetes : (e.total ?? null);
    Object.entries(CAMPOS_PREVISTOS).forEach(([lado, campos]) => {
        if (!emCustos.includes(lado) || e.valores?.ladosComBilhete?.[lado]) return;
        const valores = campos.map(c => (r && c in r ? r[c] : e[c])).filter(v => v !== null && v !== undefined && v !== '');
        if (!valores.length) return;
        previstos[lado] = valores.reduce((s, v) => s + Number(v), 0);
        total = (Number(total) || 0) + previstos[lado];
    });
    let fee = e.valores?.fee ?? null;
    if (r && 'feeCliente' in r) {
        const porBilhete = r.feeCliente ?? tela.feePadrao ?? null;
        fee = porBilhete !== null && Number(e.numTkt) > 0 ? Math.round(Number(porBilhete) * Number(e.numTkt) * 100) / 100 : null;
    }
    return { total, fee, comFee: total === null && fee === null ? null : (Number(total) || 0) + (Number(fee) || 0), previstos };
};
// Os status das linhas do participante como a tela os mostra (`rotuloNaTela`), com o rascunho de quem
// está nela: o filtro e a contagem seguem o que se vê, antes do Salvar e do Enviar. Na tela do RSVP, o
// pedido novo (`comPedidoNaTela`) já conta como a linha que o envio abre, em A EMITIR. A marca de
// cancelamento do RSVP entra junto, como pseudo-status, gravada ou no rascunho dele (`tela.cancelamentos`).
const rotulosDasLinhas = (l, tela = {}) => {
    const comPedido = !tela.aereo && comPedidoNaTela(l, tela);
    return [
        ...(l.emissao ? entradasDe(l).map(e => rotuloNaTela(l, e, tela)) : (comPedido ? [] : [ROTULO_NAO_SOLICITADO])),
        ...(comPedido ? ['EMITIR'] : []),
        // O cancelamento salvo que a tela tirou ("Tirar o cancelamento", null) já não conta, como na linha.
        ...entradasDe(l).filter(e => e.canceladoRsvp && !(e.id === l.emissao?.id && tela.cancelamentos?.[l.id] === null))
            .map(() => ROTULO_CANCELAMENTO_SOLICITADO),
        ...(tela.cancelamentos?.[l.id] && !l.emissao?.canceladoRsvp ? [ROTULO_CANCELAMENTO_SOLICITADO] : []),
    ];
};
// "23/01" de "2026-01-23 10:00:00": a data da faixa da requisição.
const diaMes = (v) => dataHora(v).slice(0, 5);

// A cor de cada status na tela é a DELE na planilha, adaptada ao sistema: as cores da legenda são
// fundo de célula do Excel (amarelo puro, verde-claro, rosa) e não servem como texto. Mantém-se a
// matiz e a cor é escurecida e saturada até dar texto e borda legíveis; o fundo do badge é o
// pastel da cor original, como nas tags de cor dinâmica do painel. Sem cor na legenda (não
// solicitado): cinza. A planilha exportada segue com as cores dela.
const CINZA = '8a8a8a';
const hexParaHsl = (hex) => {
    const [r, g, b] = [0, 2, 4].map(i => parseInt(hex.slice(i, i + 2), 16) / 255);
    const max = Math.max(r, g, b), min = Math.min(r, g, b), l = (max + min) / 2;
    if (max === min) return [0, 0, l];
    const d = max - min;
    const s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    const h = max === r ? ((g - b) / d + (g < b ? 6 : 0)) : max === g ? (b - r) / d + 2 : (r - g) / d + 4;
    return [h / 6, s, l];
};
const hslParaHex = (h, s, l) => {
    const canal = (t) => {
        const q = l < 0.5 ? l * (1 + s) : l + s - l * s, p = 2 * l - q;
        t = (t + 1) % 1;
        const v = t < 1 / 6 ? p + (q - p) * 6 * t : t < 1 / 2 ? q : t < 2 / 3 ? p + (q - p) * (2 / 3 - t) * 6 : p;
        return Math.round(v * 255).toString(16).padStart(2, '0');
    };
    return canal(h + 1 / 3) + canal(h) + canal(h - 1 / 3);
};
export function corDaLegenda(rotulo, legenda) {
    const [fundo = CINZA, fonte] = legenda?.[rotulo] ?? [];
    // No fundo branco (o PENDENTE AUTORIZAÇÃO RSVP, vermelho no branco), quem dá a cor do status é a fonte.
    const cor = String(fundo).toUpperCase() === 'FFFFFF' && fonte ? fonte : fundo;
    const [h, s, l] = hexParaHsl(cor);
    // Clara demais para texto: desce a luminosidade e, se tem cor, garante saturação (cinza fica
    // cinza: saturar matiz zero inventaria um vermelho); escura fica como é.
    const traco = l > 0.42 ? hslParaHex(h, s > 0.15 ? Math.max(s, 0.55) : s, 0.36) : cor;
    return { tinta: '#' + fundo, traco: '#' + traco };
}

/** Tag do status: pastel da cor da planilha no fundo, a cor adaptada no texto e na borda. */
export function PillLegenda({ rotulo, legenda, style }) {
    const { tinta, traco } = corDaLegenda(rotulo, legenda);
    return (
        <span className="fin-pp__tag" style={{ backgroundColor: tinta + '1f', color: traco, borderColor: traco + '55', whiteSpace: 'normal', ...style }}>
            {rotulo}
        </span>
    );
}

/**
 * Contagem por status: só o que tem ao menos 1, na ordem da legenda; clique filtra, de novo desfaz.
 * Conta linhas (entradas) pelo status que a tela mostra (`rotulosDasLinhas`), com o rascunho de quem
 * está nela: no Aéreo, o status escolhido e o Emitido que o envio vai dar, antes do Salvar e do Enviar;
 * no RSVP, o das linhas que o Aéreo já enviou (a nova conta como A EMITIR), o pedido que o próprio RSVP
 * salvou e a marca de cancelamento dele, gravada ou não. Com rascunho, o número pode não bater com o
 * Contador da planilha baixada, que é a do banco. O CANCELADO genérico e os pseudo-status (não
 * solicitado, cancelamento solicitado) não estão no Contador.
 * `tela`: { aereo, rascunhos, cancelamentos } — o rascunho de quem está na tela.
 * `direita`: o que vai no fim da barra, alinhado à direita — o badge do Consolidado, na tela do Aéreo.
 */
export function ChipsLegenda({ linhas, legenda, filtro, onFiltrar, tela = null, direita = null, conferir = 0 }) {
    const cores = useMemo(() => ({ ...(legenda ?? {}), ...CORES_PSEUDO }), [legenda]);
    const contagens = useMemo(() => {
        const n = {};
        linhas.forEach(l => rotulosDasLinhas(l, tela ?? {}).forEach(r => { n[r] = (n[r] ?? 0) + 1; }));
        return [...Object.keys(legenda ?? {}), ROTULO_NAO_SOLICITADO, ROTULO_CANCELAMENTO_SOLICITADO].filter(r => n[r]).map(r => [r, n[r]]);
    }, [linhas, legenda, tela]);
    // A cada recarga da tela (`conferir`: ao abrir, e depois do Salvar e do Enviar), o status filtrado que
    // ficou sem linha sai do filtro: senão a tabela fica vazia sem dizer por quê. Durante a edição, não.
    useEffect(() => {
        if (conferir && filtro && !contagens.some(([r]) => r === filtro)) onFiltrar(null);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [conferir]);
    // Com filtro ativo a faixa fica: o status filtrado pode ter saído da tela, e só o Limpar status o tira.
    if (!contagens.length && !direita && !filtro) return null;
    return (
        <div>
            <p className="fin-pp__cphead-sub" style={{ margin: '0 0 6px' }}>
                {filtro ? 'Filtrando por status — clique de novo para remover:' : 'Filtrar por status:'}
            </p>
            <div className="fin-pp__statusbar">
                {contagens.map(([rotulo, quantidade]) => {
                    // Mesmo chip do painel de participantes: bolinha, contagem e o filtro ativo mais forte.
                    const { tinta, traco } = corDaLegenda(rotulo, cores);
                    const ativo = filtro === rotulo;
                    return (
                        <button type="button" key={rotulo} className="fin-pp__statuschip" aria-pressed={ativo}
                            title={ativo ? 'Clique para remover o filtro' : 'Filtrar por ' + rotulo}
                            onClick={() => onFiltrar(ativo ? null : rotulo)}
                            style={{
                                cursor: 'pointer',
                                backgroundColor: tinta + (ativo ? '3d' : '1f'),
                                color: traco,
                                borderColor: traco + (ativo ? 'ff' : '55'),
                                fontWeight: ativo ? 700 : undefined,
                            }}>
                            <span className="fin-pp__statuschip-dot" style={{ backgroundColor: traco }} />
                            <span className="fin-pp__statuschip-num">{quantidade}</span> {rotulo}
                        </button>
                    );
                })}
                {/* O mesmo do painel de participantes: aparece com um status filtrado. */}
                {filtro && (
                    <button type="button" className="fin-pp__btn fin-pp__btn--sm" onClick={() => onFiltrar(null)}>
                        <FaTimes size={12} /> Limpar status
                    </button>
                )}
                {direita && <span style={{ marginLeft: 'auto' }}>{direita}</span>}
            </div>
        </div>
    );
}

// O Consolidado do evento, como a aba Consolidado da planilha do Aéreo: os convidados emitidos — por
// status, as linhas em EMITIDO como a tela as mostra (`rotuloNaTela`, com o Emitido que o Enviar vai dar),
// como o COUNTIF dela, e não o TKT emitidos, que conta bilhetes — e os valores de todas as entradas: o
// aéreo é o total sem FEE, e o geral, aéreo + FEE + seguro. Por enquanto só há o nacional: o internacional
// e o seguro chegam com a emissão internacional. `tela`: { aereo, rascunhos, feePadrao }.
export const consolidadoDe = (linhas, tela = {}) => {
    // Com a reserva prevista e o FEE do rascunho da tela, como a linha (`valorNaTela`).
    const valores = (linhas ?? []).flatMap(l => entradasDe(l).map(e => valorNaTela(l, e, tela)));
    const aereo = valores.reduce((s, v) => s + (Number(v.total) || 0), 0);
    const fee = valores.reduce((s, v) => s + (Number(v.fee) || 0), 0);
    const emitidos = (linhas ?? []).flatMap(l => entradasDe(l).filter(e => rotuloNaTela(l, e, tela) === 'EMITIDO')).length;
    return { emitidos, aereo, fee, geral: aereo + fee };
};

/**
 * O badge do Consolidado, no fim da barra do filtro por status: com a planilha à vista, mostra o geral
 * do evento em R$ e abre o Consolidado; aberto, mostra quantas emissões há e, clicado, volta à planilha.
 */
export function BadgeConsolidado({ linhas, tela, ativo, onAlternar }) {
    const c = consolidadoDe(linhas, tela);
    return (
        <button type="button" className="fin-pp__statuschip" aria-pressed={ativo} onClick={onAlternar}
            title={ativo ? 'Clique para voltar à planilha' : 'Abrir o Consolidado do evento'}
            style={{
                cursor: 'pointer',
                backgroundColor: ativo ? 'var(--fin-acw)' : 'var(--fin-sf2)',
                color: 'var(--fin-ac)',
                borderColor: ativo ? 'var(--fin-ac)' : 'var(--fin-acb)',
                fontWeight: ativo ? 700 : undefined,
            }}>
            <span className="fin-pp__statuschip-num">{ativo ? c.emitidos : brl(c.geral)}</span>
            {ativo ? (c.emitidos === 1 ? 'emissão' : 'emissões') : 'Consolidado'}
        </button>
    );
}

/**
 * O Consolidado no lugar da planilha: o quadro CONSOLIDADO GERAL da planilha do Aéreo, com os convidados
 * emitidos e os valores do evento inteiro. O internacional e o seguro ficam com "—" até a emissão
 * internacional existir.
 */
export function ConsolidadoPlanilha({ linhas, tela }) {
    const c = consolidadoDe(linhas, tela);
    const secoes = [
        ['Convidados emitidos', [
            ['Nacional', String(c.emitidos)],
            ['Internacional', '—'],
            ['Total de convidados', String(c.emitidos), true],
        ]],
        ['Valores', [
            ['Aéreo nacional', brl(c.aereo)],
            ['Aéreo internacional', '—'],
            ['Total geral aéreo', brl(c.aereo), true],
            ['Seguro', '—'],
            ['FEE', brl(c.fee)],
            ['Geral (aéreo + FEE + seguro)', brl(c.geral), true],
        ]],
    ];
    return (
        <table className="fin-pp__tbl" style={{ maxWidth: 560 }}>
            <thead><tr><th colSpan={2}>Consolidado geral</th></tr></thead>
            <tbody>
                {secoes.map(([titulo, itens]) => (
                    <React.Fragment key={titulo}>
                        <tr className="fin-pp__tbl-sub"><td colSpan={2}><span className="fin-pp__sub-name">{titulo}</span></td></tr>
                        {itens.map(([rotulo, valor, total]) => (
                            <tr key={rotulo}>
                                <td className={total ? 'fin-pp__cell-strong' : undefined}>{rotulo}</td>
                                <td className={'text-end' + (total ? ' fin-pp__cell-strong' : '')} style={{ whiteSpace: 'nowrap' }}>{valor}</td>
                            </tr>
                        ))}
                    </React.Fragment>
                ))}
            </tbody>
        </table>
    );
}

/**
 * Filtro por requisição: um select com todas as requisições do evento e quantas linhas (entradas) cada uma
 * abriu. `contarEm`: as linhas que a contagem olha — as que passam nos outros filtros (`passaNosFiltros`).
 */
export function SelectRequisicao({ linhas, contarEm = linhas, requisicoes, filtro, onFiltrar }) {
    if (!(requisicoes ?? []).length) return null;
    const contagem = (id) => contarEm.reduce((n, l) => n + entradasDe(l).filter(e => e.requisicao?.id === id).length, 0);
    const opcoes = [
        { value: '', label: 'Todas as requisições' },
        ...requisicoes.map(r => ({
            value: r.id,
            label: `${r.ordinal} requisição · ${dataHora(r.solicitadoEm)}${r.solicitadoPor ? ` · ${r.solicitadoPor}` : ''} (${contagem(r.id)})`,
        })),
    ];
    return (
        <div className="fin-pp__field fin-pp__field--auto" style={{ minWidth: 320 }}>
            <label className="fin-pp__label">Requisição</label>
            <ReactSelect options={opcoes} placeholder="Todas as requisições"
                value={{ value: filtro ?? '' }}
                onChange={o => onFiltrar(o && o.value ? o.value : null)} />
        </div>
    );
}

export const SEM_ORIGEM = 'Sem origem';

/**
 * Filtro por origem: as cidades de onde os participantes saem (`origemDe`), com quantos saem de cada. As
 * opções são as de `linhas`; a contagem, a de `contarEm` (as que passam nos outros filtros), e pode dar 0.
 */
export function SelectOrigem({ linhas, contarEm = linhas, filtro, onFiltrar }) {
    if (!(linhas ?? []).length) return null;
    const contagem = {};
    linhas.forEach(l => { contagem[origemDe(l) ?? SEM_ORIGEM] = 0; });
    contarEm.forEach(l => { const o = origemDe(l) ?? SEM_ORIGEM; contagem[o] = (contagem[o] ?? 0) + 1; });
    const origens = Object.keys(contagem)
        .sort((a, b) => (a === SEM_ORIGEM) - (b === SEM_ORIGEM) || a.localeCompare(b, 'pt-BR'));
    const opcoes = [
        { value: '', label: 'Todas as origens' },
        ...origens.map(o => ({ value: o, label: `${o} (${contagem[o]})` })),
    ];
    return (
        <div className="fin-pp__field fin-pp__field--auto" style={{ minWidth: 240 }}>
            <label className="fin-pp__label">Origem</label>
            <ReactSelect options={opcoes} placeholder="Todas as origens"
                value={{ value: filtro ?? '' }}
                onChange={o => onFiltrar(o && o.value ? o.value : null)} />
        </div>
    );
}

/**
 * Filtro por voo, na tela do Aéreo: um select com os voos da aba já agrupados (dia, companhia, número e o
 * caminho, com quantos participantes vão no voo) preenche a companhia, o número e a data, e os três também
 * se ajustam à mão. Olha a linha atual de cada participante (`voosAtuais`). `filtro`: { cia, voo, data }.
 * Os voos são os de `linhas`; quantos vão em cada, os de `contarEm` (as que passam nos outros filtros).
 */
export function FiltroVoo({ linhas, contarEm = linhas, filtro, onFiltrar }) {
    const f = filtro ?? FILTRO_VOO_VAZIO;
    const grupos = useMemo(() => {
        const mapa = new Map();
        // Cada participante conta uma vez por voo, mesmo com o voo na ida e na volta.
        const somar = (lista, conta) => (lista ?? []).forEach(l => {
            new Map(voosAtuais(l).map(p => [chaveDoVoo(p), p])).forEach((p, chave) => {
                const g = mapa.get(chave) ?? { ...p, n: 0 };
                if (conta) g.n += 1;
                mapa.set(chave, g);
            });
        });
        somar(linhas, false);
        somar(contarEm, true);
        return [...mapa.values()].sort((a, b) => dataIso(a.data).localeCompare(dataIso(b.data))
            || a.cia.localeCompare(b.cia, 'pt-BR') || a.voo.localeCompare(b.voo, 'pt-BR', { numeric: true }));
    }, [linhas, contarEm]);
    const unicos = (campo) => [...new Set(grupos.map(g => g[campo]).filter(Boolean))];
    const cias = unicos('cia').sort((a, b) => a.localeCompare(b, 'pt-BR'));
    const datas = unicos('data').sort((a, b) => dataIso(a).localeCompare(dataIso(b)));
    const escolhido = grupos.find(g => g.cia === f.cia && g.voo === String(f.voo ?? '').trim() && g.data === f.data);
    const mudar = (campos) => onFiltrar({ ...f, ...campos });
    return (
        <div className="fin-pp__filters">
            <div className="fin-pp__field fin-pp__field--grow">
                <label className="fin-pp__label">Voo</label>
                <ReactSelect placeholder="Todos os voos"
                    options={[{ value: '', label: 'Todos os voos' }, ...grupos.map(g => ({ value: chaveDoVoo(g), label: rotuloDoVoo(g) }))]}
                    value={{ value: escolhido ? chaveDoVoo(escolhido) : '' }}
                    onChange={o => {
                        const g = o && grupos.find(x => chaveDoVoo(x) === o.value);
                        onFiltrar(g ? { cia: g.cia, voo: g.voo, data: g.data } : FILTRO_VOO_VAZIO);
                    }} />
            </div>
            <div className="fin-pp__field">
                <label className="fin-pp__label">Companhia</label>
                <ReactSelect placeholder="Todas" options={[{ value: '', label: 'Todas' }, ...cias.map(c => ({ value: c, label: c }))]}
                    value={{ value: f.cia }} onChange={o => mudar({ cia: o ? o.value : '' })} />
            </div>
            <div className="fin-pp__field">
                <label className="fin-pp__label">Nº do voo</label>
                <FinInput value={f.voo} placeholder="Ex.: 3057" onChange={e => mudar({ voo: e.target.value })} />
            </div>
            <div className="fin-pp__field">
                <label className="fin-pp__label">Data</label>
                <ReactSelect placeholder="Todas" options={[{ value: '', label: 'Todas' }, ...datas.map(d => ({ value: d, label: d }))]}
                    value={{ value: f.data }} onChange={o => mudar({ data: o ? o.value : '' })} />
            </div>
            {filtroVooAtivo(f) && (
                <div className="fin-pp__filters-actions">
                    <button type="button" className="fin-pp__btn" onClick={() => onFiltrar(FILTRO_VOO_VAZIO)}>
                        <FaTimes size={12} style={{ marginRight: 6 }} /> Limpar voo
                    </button>
                </div>
            )}
        </div>
    );
}

// O nome da aba completa, sem um dado pelo Aéreo, quando há outras: na tela; no Excel, "Emissão (Todos Participantes)".
const TODOS_PARTICIPANTES = 'Todos Participantes';

/**
 * As abas da planilha, como as do Excel baixado: a Todos Participantes e as que o Aéreo criou no evento (`abas`:
 * { id, nome, ano, tipos }). É o rodapé da tabela, preso embaixo enquanto ela está à vista, logo acima do rodapé
 * fixo da tela (`.fixoRodape`, medido: a altura dele muda com os botões). `linhas`: as do evento, que dizem os
 * anos e os tipos que há; `contarEm`: as da tela sem os filtros, porque cada aba mostra o total dela. `aba`: a
 * escolhida, ou null para a Todos.
 * `onNova()` e `onExcluir(aba)`: só no Aéreo, que cria e exclui no rascunho da tela, até o Salvar. A aba ainda não
 * salva vem com `nova`, e o X a tira; a salva que sai no Salvar vem com `excluir`, e o mesmo botão a mantém. O
 * "+" aparece com mais de um ano ou mais de um tipo no evento: com um de cada, toda aba nova teria as linhas da
 * Todos. A de filtro vazio entre as `abas` não é aba: é o nome da Todos (`ehDaTodos`). Sem nome e sozinha na
 * barra, a Todos se chama Emissão, como a aba do Excel quando não há criada. `onNomearTodos(nome)`: só no Aéreo,
 * o clique na Todos já escolhida abre o modal do nome dela; com nome, ela tem o X das outras, que tira o nome, e a
 * linha de filtro vazio sai no Salvar.
 */
export function AbasDaPlanilha({ abas, linhas, contarEm = linhas, aba, onAba, onNova, onExcluir, onNomearTodos }) {
    const [embaixo, setEmbaixo] = useState(0);
    useEffect(() => {
        const rodape = document.querySelector('.fixoRodape');
        const medir = () => {
            const pos = rodape ? getComputedStyle(rodape).position : '';
            setEmbaixo(pos === 'fixed' || pos === 'sticky' ? Math.round(rodape.getBoundingClientRect().height) : 0);
        };
        medir();
        window.addEventListener('resize', medir);
        const observador = rodape && typeof ResizeObserver !== 'undefined' ? new ResizeObserver(medir) : null;
        if (observador) observador.observe(rodape);
        return () => { window.removeEventListener('resize', medir); if (observador) observador.disconnect(); };
    }, []);
    const { anos, tipos } = useMemo(() => anosETiposDe(linhas), [linhas]);
    const criadas = (abas ?? []).filter(a => !ehDaTodos(a));
    // O nome da Todos (`ehDaTodos`): o que vale ou, sem ele, o salvo que sai no Salvar, que o X mantém.
    const daTodos = (abas ?? []).find(a => ehDaTodos(a) && !a.excluir);
    const daTodosSaindo = daTodos ? null : (abas ?? []).find(a => ehDaTodos(a) && a.excluir);
    const nomeDaTodos = daTodos?.nome;
    const comNova = !!onNova && (anos.length > 1 || tipos.length > 1);
    if (!(linhas ?? []).length && !criadas.length) return null;
    // O filtro da aba, no title: "2026 · Staff, Staff Access".
    const descricao = (a) => `${a.ano ?? 'Todos os anos'} · ${(a.tipos ?? []).length
        ? a.tipos.map(id => tipos.find(t => t.id === String(id))?.nome ?? `Tipo ${id}`).join(', ') : 'todos os tipos'}`;
    const rotuloTodos = <>{nomeDaTodos ?? daTodosSaindo?.nome ?? (criadas.length || comNova ? TODOS_PARTICIPANTES : 'Emissão')} ({(contarEm ?? []).length})</>;
    const cliqueTodos = {
        'aria-pressed': !aba,
        title: onNomearTodos && !aba ? 'Clique de novo para dar nome a esta aba, também no Excel baixado' : undefined,
        onClick: () => (onNomearTodos && !aba ? onNomearTodos(nomeDaTodos) : onAba(null)),
    };
    const comNome = onExcluir ? (daTodos ?? daTodosSaindo) : null;
    const acaoTodos = daTodosSaindo ? `Manter o nome ${daTodosSaindo.nome}` : `Tirar o nome ${nomeDaTodos}`;
    return (
        <div className="fin-pp__abas-planilha" style={{ bottom: embaixo }}>
            {comNome ? (
                <span className={'fin-pp__aba-planilha fin-pp__aba-planilha--grupo' + (!aba ? ' is-active' : '')
                    + (daTodos?.nova ? ' is-nova' : '') + (daTodosSaindo ? ' is-excluida' : '')}>
                    <button type="button" className="fin-pp__aba-planilha-rotulo" {...cliqueTodos}>{rotuloTodos}</button>
                    <button type="button" className="fin-pp__aba-planilha-x" title={acaoTodos} aria-label={acaoTodos} onClick={() => onExcluir(comNome)}>
                        {daTodosSaindo ? <FaUndo size={10} /> : <FaTimes size={10} />}
                    </button>
                </span>
            ) : (
                <button type="button" className={'fin-pp__aba-planilha' + (!aba ? ' is-active' : '')} {...cliqueTodos}>{rotuloTodos}</button>
            )}
            {criadas.map(a => {
                const acao = a.excluir ? `Manter a aba ${a.nome}` : a.nova ? `Tirar a aba nova ${a.nome}` : `Excluir a aba ${a.nome}`;
                return (
                    <span key={a.id} className={'fin-pp__aba-planilha fin-pp__aba-planilha--grupo' + (aba?.id === a.id ? ' is-active' : '')
                        + (a.nova ? ' is-nova' : '') + (a.excluir ? ' is-excluida' : '')}
                        title={(a.nova ? 'Nova, grava no Salvar · ' : a.excluir ? 'Sai no Salvar · ' : '') + descricao(a)}>
                        <button type="button" className="fin-pp__aba-planilha-rotulo" aria-pressed={aba?.id === a.id} onClick={() => onAba(a)}>
                            {a.nome} ({(contarEm ?? []).filter(l => casaAba(l, a)).length})
                        </button>
                        {onExcluir && (
                            <button type="button" className="fin-pp__aba-planilha-x" title={acao} aria-label={acao} onClick={() => onExcluir(a)}>
                                {a.excluir ? <FaUndo size={10} /> : <FaTimes size={10} />}
                            </button>
                        )}
                    </span>
                );
            })}
            {comNova && (
                <button type="button" className="fin-pp__aba-planilha fin-pp__aba-planilha--nova" onClick={onNova}
                    title="Nova aba: as linhas de um ano ou de alguns tipos de participante, também no Excel baixado">
                    <FaPlus size={10} />{criadas.length ? null : 'Nova Aba'}
                </button>
            )}
        </div>
    );
}

/**
 * O modal do "+" das abas da planilha (só no Aéreo): o ano (`anoDoParticipante`), os tipos de participante e o
 * nome. Os anos e os tipos são os que há nas `linhas` (as do evento); com um ano só, ele já vem escolhido, sem
 * "Todos". O nome pode ficar vazio quando há um sugerido, que é o placeholder: o tipo, quando é um só; o ano,
 * quando é um entre vários e os tipos são todos ou mais de um. Fora disso, ele é obrigatório. Filtro igual ao de
 * uma aba que fica (`abas`), ou ao da Todos Participantes, não entra. O Aplicar põe a aba no rascunho da tela
 * (`onAplicar({ nome, ano, tipos })`), e ela grava no Salvar. Com `todos`, é o modal do nome da Todos
 * Participantes: ano e tipos em Todos, travados, e só o nome se edita, a partir do que ela tem (`nomeAtual`).
 */
export function NovaAba({ linhas, abas, todos = false, nomeAtual = '', onAplicar, onFechar }) {
    const { anos, tipos: doEvento } = useMemo(() => anosETiposDe(linhas), [linhas]);
    const [ano, setAno] = useState(() => (!todos && anos.length === 1 ? anos[0].ano : null));
    const [tipos, setTipos] = useState([]);
    const [nome, setNome] = useState(nomeAtual ?? '');
    const sugerido = todos ? '' : tipos.length === 1 ? (doEvento.find(t => t.id === tipos[0])?.nome ?? '')
        : anos.length > 1 && ano ? String(ano) : '';
    const nomeFinal = nome.trim() || sugerido;
    // Todos os anos e todos os tipos é a Todos Participantes: entra no mesmo bloqueio do filtro repetido, com o nome
    // dela (o que o Aéreo deu, que vem nas `abas`, antes do padrão).
    const igual = todos ? null : [...(abas ?? []), { nome: TODOS_PARTICIPANTES, ano: null, tipos: [] }]
        .find(a => chaveDaAba(a.ano, a.tipos) === chaveDaAba(ano, tipos));
    const erro = igual ? `A aba ${igual.nome} já tem este ano e estes tipos.` : null;
    const opcoesAno = [...(anos.length === 1 && !todos ? [] : [{ value: 'todos', label: 'Todos' }]),
        ...anos.map(a => ({ value: String(a.ano), label: `${a.ano} (${a.n})` }))];
    const opcoesTipo = doEvento.map(t => ({ value: t.id, label: `${t.nome} (${t.n})` }));
    return (
        <ConfirmarAcaoModal aberto titulo={todos ? TODOS_PARTICIPANTES : 'Nova aba'}
            subtitulo={todos ? 'O nome da aba com todos os participantes, na tela do Aéreo, na do RSVP e no Excel baixado. Grava no Salvar.'
                : 'Filtra a planilha na tela do Aéreo e na do RSVP, e vira uma aba no Excel baixado. Grava no Salvar.'}
            rotulo="Aplicar" icone="ok" confirmarPreso={!!erro || !nomeFinal || (todos && nomeFinal === nomeAtual)}
            onConfirmar={() => onAplicar({ nome: nomeFinal, ano, tipos })} onFechar={onFechar}>
            <div className="fin-pp__filters">
                <FinField full label={(
                    <>
                        Ano{' '}
                        <IconeInfo rotulo="Ano" texto="Data Emissão ou então Data Aprovação" cor="var(--fin-ft)">
                            <FaInfoCircle size={12} />
                        </IconeInfo>
                    </>
                )}>
                    <ReactSelect options={opcoesAno} value={{ value: ano ? String(ano) : 'todos' }} isDisabled={todos}
                        onChange={e => setAno(e && e.value !== 'todos' ? Number(e.value) : null)} />
                </FinField>
                <FinField label="Tipo de participante" full>
                    <ReactSelect isMulti options={opcoesTipo} placeholder="Todos os tipos" isDisabled={todos}
                        value={opcoesTipo.filter(o => tipos.includes(o.value))}
                        onChange={escolhidos => setTipos((escolhidos ?? []).map(o => o.value))} />
                </FinField>
                <FinField label="Nome da Aba" required={!sugerido} full>
                    <FinInput value={nome} maxLength={31} placeholder={sugerido || (todos ? TODOS_PARTICIPANTES : 'Ex.: Emissão')} onChange={e => setNome(e.target.value)} />
                </FinField>
            </div>
            {erro && (
                <div className="fin-pp__notifica is-warn" style={{ marginTop: 12 }}>
                    <FaExclamationTriangle size={13} style={{ flex: 'none', marginTop: 3 }} />
                    <span>{erro}</span>
                </div>
            )}
        </ConfirmarAcaoModal>
    );
}

/**
 * Os totais da planilha, calculados das linhas — todas as entradas, as anteriores também, como as
 * somas da planilha exportada. "Total" é com o FEE da Access; o TKT médio é sem ele, como a fórmula do rodapé da
 * planilha (o TOTAL sem FEE pelo divisor), porque é o custo do bilhete que se compara com o teto do cliente.
 * `so`: os rótulos a mostrar — a tela do RSVP mostra o TKT médio e o teto; TKT emitidos, reembolso,
 * FEE e os totais ficam com o Aéreo. `tela`: a do status que cada linha mostra (`rotuloNaTela`) e a da reserva
 * prevista e do FEE (`valorNaTela`). `sla`: { emissao, antecedencia }, os prazos do pedido, só na família Sanofi —
 * os cards ficam ao lado do teto; null, sem eles.
 */
export function TotaisPlanilha({ linhas, tetoTktMedio, so = null, tela = {}, sla = null }) {
    const emissoes = linhas.flatMap(entradasDe);
    const soma = (de) => emissoes.reduce((s, e) => s + (Number(de(e)) || 0), 0);
    const valores = linhas.flatMap(l => entradasDe(l).map(e => valorNaTela(l, e, tela)));
    const totalSemFee = valores.reduce((s, v) => s + (Number(v.total) || 0), 0);
    const fee = valores.reduce((s, v) => s + (Number(v.fee) || 0), 0);
    const total = totalSemFee + fee;
    // O divisor é o do rodapé da planilha: quem está em aprovação de custos, emitido ou cancelado
    // por alteração da ida, pelo status que a linha mostra — na tela do Aéreo, o do rascunho dele e o
    // Emitido que o Enviar vai dar, sem esperar o envio ao RSVP.
    const divisor = linhas.flatMap(l => entradasDe(l).map(e => rotuloNaTela(l, e, tela)))
        .filter(r => ROTULOS_DO_TKT_MEDIO.includes(r)).length;
    // O reembolso que a linha mostra: o do rascunho da tela, quando ele tem um, como na coluna.
    const reembolso = (e) => {
        const r = tela.rascunhos?.[e.id];
        return r && 'reembolsoPrevisto' in r ? r.reembolsoPrevisto : e.reembolsoPrevisto;
    };
    // Os totais e o TKT médio ficam colados no teto do cliente, que é com o que o médio se compara.
    const itens = [
        ['TKT emitidos', String(soma(e => e.numTkt)), 'ok'],
        // O que o Aéreo informou no Cancelar com reembolso; só existe nesses cancelamentos.
        ['Reembolso previsto', brl(soma(reembolso)), 'danger'],
        ['Total FEE', brl(fee)],
        ['Total sem FEE', brl(totalSemFee)],
        ['Total', brl(total)],
        ['TKT médio', divisor ? brl(totalSemFee / divisor) : '—'],
        ['Teto TKT médio cliente', tetoTktMedio === null || tetoTktMedio === undefined ? '—' : brl(tetoTktMedio), 'info'],
        // Os prazos de SLA da Sanofi, com os nomes das colunas da planilha dela.
        ...(sla ? [
            ['SLA emissão', sla.emissao === null || sla.emissao === undefined ? '—' : `${sla.emissao} dias úteis`, 'info'],
            ['SLA antecedência do evento', sla.antecedencia === null || sla.antecedencia === undefined ? '—' : `${sla.antecedencia} dias`, 'info'],
        ] : []),
    ];
    const cor = { danger: '#c1232b', ok: '#15803d', info: '#1f3c88' };
    return (
        <div className="d-flex flex-wrap gap-2 justify-content-end">
            {itens.filter(([rotulo]) => !so || so.includes(rotulo)).map(([rotulo, valor, tom]) => (
                <div key={rotulo} style={{ border: '1px solid var(--fin-bd)', borderRadius: 10, padding: '6px 12px', minWidth: 0, background: 'var(--fin-sf2)' }}>
                    <div className="text-uppercase" style={{ color: 'var(--fin-mt)', fontSize: 10, letterSpacing: '0.05em', fontWeight: 600 }}>{rotulo}</div>
                    <div style={{ color: cor[tom] ?? 'var(--fin-tx)', fontSize: 15, fontWeight: 700, whiteSpace: 'nowrap' }}>{valor}</div>
                </div>
            ))}
        </div>
    );
}

// Ida e retorno numa coluna só, com o que se confere de relance: dia, companhia, voo, horários e o
// caminho pelas cidades. Ano e aeroporto ficam no `title`. Voo com conexão vem com os trechos
// separados por "|": o caminho vira "São Paulo ➝ Frankfurt ➝ Berlim", e os horários saem perna a
// perna, na ordem dos voos. Do que foi gravado sai o LOC e o bilhete, uma vez só quando os dois
// lados são o mesmo bilhete (ida e volta num PDF só).
const partesDoVoo = (t) => String(t ?? '').split('|').map(s => s.trim()).filter(Boolean);
// A cidade da perna nos dois formatos que a Importação já gravou: "São Paulo (Guarulhos)", desde
// 28/10/2026, e "São Paulo - Guarulhos", antes.
const cidadeDe = (t) => t.split(' - ')[0].replace(/\s*\([^()]*\)$/, '').trim();
// A origem do participante, para o filtro: a cidade de onde sai a ida escolhida (a primeira perna); sem
// ida, a cidade a que o retorno chega (a última perna). Sem escolha, nenhuma.
export const origemDe = (l) => {
    const ida = partesDoVoo(l?.escolhaIda?.origem);
    if (ida.length) return cidadeDe(ida[0]) || null;
    const volta = partesDoVoo(l?.escolhaRetorno?.destino);
    return volta.length ? cidadeDe(volta[volta.length - 1]) || null : null;
};
// Um lado da escolha, perna a perna: companhia, número e data de cada voo, com as cidades de saída e de
// chegada para o rótulo. O campo com um valor só para a conexão inteira (a companhia, a data do dia) vale
// para todas as pernas.
const pernasDoLado = (escolha) => {
    if (!escolha) return [];
    const cias = partesDoVoo(escolha.companhia);
    const datas = partesDoVoo(escolha.data);
    const origens = partesDoVoo(escolha.origem);
    const destinos = partesDoVoo(escolha.destino);
    return partesDoVoo(escolha.voo).map((voo, i) => ({
        cia: cias[i] ?? cias[0] ?? '',
        voo,
        data: datas[i] ?? datas[0] ?? '',
        origem: cidadeDe(origens[i] ?? destinos[i - 1] ?? ''),
        destino: cidadeDe(destinos[i] ?? ''),
    }));
};
// Os voos da linha atual do participante, ida e volta, como a coluna Voos os mostra ao Aéreo: a escolha; na
// linha com o voo trocado depois da requisição, os voos pedidos. O filtro por voo olha só a atual: as
// anteriores são o histórico, e o voo que se confere é o de agora.
export const voosAtuais = (l) => {
    const fonte = l?.emissao?.trocouEscolha ? l.emissao : (l ?? {});
    return [...(fonte.naoUtilizaIda ? [] : pernasDoLado(fonte.escolhaIda)),
        ...(fonte.naoUtilizaRetorno ? [] : pernasDoLado(fonte.escolhaRetorno))];
};
// O filtro por voo: companhia, número e data, cada um opcional. Casa a perna que bate em todos os
// preenchidos — companhia e data iguais, o número contendo o que foi digitado.
export const FILTRO_VOO_VAZIO = { cia: '', voo: '', data: '' };
const soNumero = (t) => String(t ?? '').toUpperCase().replace(/\s+/g, '');
export const filtroVooAtivo = (f) => !!(f && (f.cia || soNumero(f.voo) || f.data));
const casaVoo = (p, f) => (!f.cia || p.cia === f.cia)
    && (!soNumero(f.voo) || soNumero(p.voo).includes(soNumero(f.voo)))
    && (!f.data || p.data === f.data);
const chaveDoVoo = (p) => [p.cia, p.voo, p.data].join('|');
// "18/05/2026" → "18/05"; "2026-05-18", para ordenar.
const diaDoVoo = (d) => String(d ?? '').replace(/^(\d{2}\/\d{2})\/\d{4}$/, '$1');
const dataIso = (d) => String(d ?? '').replace(/^(\d{2})\/(\d{2})\/(\d{4})$/, '$3-$2-$1');
const rotuloDoVoo = (g) => `${diaDoVoo(g.data)} · ${g.cia} ${g.voo}${g.origem || g.destino ? ` · ${g.origem} ➝ ${g.destino}` : ''} (${g.n})`;
// O filtro por voo em texto curto, para o botão que o abre: "LATAM 3057 · 18/05".
export const resumoFiltroVoo = (f) => [[f?.cia, String(f?.voo ?? '').trim()].filter(Boolean).join(' '), diaDoVoo(f?.data)]
    .filter(Boolean).join(' · ');
// O ano de uma linha: o da DATA EMISSÃO (o último bilhete gravado nela) ou, sem bilhete gravado, o da DATA
// APROVAÇÃO (a requisição). Null sem nenhum dos dois.
const anoDaLinha = (e) => {
    const emitidaEm = (e?.bilhetes ?? []).filter(b => b.situacao === 'gravado').map(b => b.emitidoEm).filter(Boolean).sort().pop();
    return Number(String(emitidaEm || e?.requisicao?.solicitadoEm || '').slice(0, 4)) || null;
};
// O ano do participante nas abas: o da primeira linha dele, que dita o agrupamento. As linhas dele vão sempre
// juntas, na tela e na planilha baixada (`EventoAereoEmissaoService::anoDoParticipante`).
const anoDoParticipante = (l) => anoDaLinha(entradasDe(l)[0]);
// A aba criada no evento ({ ano, tipos }) pega o participante de um dos tipos dela (ou de qualquer tipo) cuja
// primeira linha é do ano dela (ou de qualquer ano).
const casaAba = (l, aba) => (!(aba.tipos ?? []).length || aba.tipos.includes(String(l?.tipo?.id ?? '')))
    && (!aba.ano || anoDoParticipante(l) === aba.ano);
// O filtro de uma aba em texto, "ano|tipos", como o do back (`EventoAereoAba::chaveDoFiltro`): dois iguais são a
// mesma aba.
export const chaveDaAba = (ano, tipos) => `${ano ?? '*'}|${[...new Set((tipos ?? []).map(String))]
    .sort((a, b) => a.localeCompare(b, undefined, { numeric: true })).join(',')}`;
// A de filtro vazio (todos os anos e todos os tipos) não filtra: é o nome que o Aéreo deu à Todos Participantes, uma
// por evento (`EventoAereoAba::ehDaTodos`).
export const ehDaTodos = (a) => chaveDaAba(a.ano, a.tipos) === chaveDaAba(null, []);
// Os anos (`anoDoParticipante`) e os tipos de participante que há nas linhas, cada um com quantos participantes
// tem: o que a aba nova pode filtrar. O participante sem ano ou sem tipo não dá um nem outro.
const anosETiposDe = (linhas) => {
    const porAno = new Map();
    const porTipo = new Map();
    (linhas ?? []).forEach(l => {
        const ano = anoDoParticipante(l);
        if (ano) porAno.set(ano, (porAno.get(ano) ?? 0) + 1);
        const id = String(l?.tipo?.id ?? '');
        if (!id) return;
        const t = porTipo.get(id) ?? { id, nome: l.tipo.nome || `Tipo ${id}`, n: 0 };
        t.n += 1;
        porTipo.set(id, t);
    });
    return {
        anos: [...porAno.entries()].sort((a, b) => a[0] - b[0]).map(([ano, n]) => ({ ano, n })),
        tipos: [...porTipo.values()].sort((a, b) => a.id.localeCompare(b.id, undefined, { numeric: true })),
    };
};
// Os filtros da planilha, que valem todos juntos: o status (as chips), o participante (a busca), a origem,
// a requisição, o voo e a aba do rodapé (`AbasDaPlanilha`). Cada um diz quando está ligado e se a linha passa
// nele. `tela`: a de `rotulosDasLinhas`. A ordem é a de quem sai primeiro quando dois ficam sem linha em comum
// (`filtrosSemEquivalente`): a aba do rodapé é a última.
export const FILTROS_DA_EMISSAO = {
    status:     { ativo: (v) => !!v, casa: (l, v, tela) => rotulosDasLinhas(l, tela).includes(v) },
    busca:      { ativo: (v) => !!String(v ?? '').trim(), casa: (l, v) => String(l.nome ?? '').toLowerCase().includes(String(v).trim().toLowerCase()) },
    origem:     { ativo: (v) => !!v, casa: (l, v) => (origemDe(l) ?? SEM_ORIGEM) === v },
    requisicao: { ativo: (v) => !!v, casa: (l, v) => entradasDe(l).some(e => e.requisicao?.id === v) },
    voo:        { ativo: (v) => filtroVooAtivo(v), casa: (l, v) => voosAtuais(l).some(p => casaVoo(p, v)) },
    aba:        { ativo: (v) => !!v, casa: (l, v) => casaAba(l, v) },
};
// A linha passa em todos os filtros ligados; com `exceto`, em todos menos esse — é com essas linhas que um
// filtro conta as opções dele, e é assim que a escolha num propaga para os outros.
export const passaNosFiltros = (l, filtros, tela, exceto = null) => Object.entries(FILTROS_DA_EMISSAO)
    .every(([chave, f]) => chave === exceto || !f.ativo(filtros[chave]) || f.casa(l, filtros[chave], tela));
// Os filtros que a tela limpa quando `chave` muda (`novos`, já com o valor novo): os ligados que ficaram sem
// nenhuma linha equivalente. Só quando havia linha para eles escolherem: sem linha nenhuma nos outros (a
// busca digitada pela metade), limpar não faria aparecer ninguém. A busca, que é o que se digitou, nunca sai.
export const filtrosSemEquivalente = (linhas, novos, chave, tela) => {
    const atual = { ...novos };
    return Object.keys(FILTROS_DA_EMISSAO).filter(outro => {
        const f = FILTROS_DA_EMISSAO[outro];
        if (outro === chave || outro === 'busca' || !f.ativo(atual[outro])) return false;
        const base = (linhas ?? []).filter(l => passaNosFiltros(l, atual, tela, outro));
        if (!base.length || base.some(l => f.casa(l, atual[outro], tela))) return false;
        atual[outro] = null;
        return true;
    });
};
// Saída–chegada de cada perna, na posição dela: a perna do meio sem hora fica, com --:--, para a
// hora da seguinte não subir para ela. Perna vazia no fim sai: um "|" sobrando no fim do texto não
// vira perna. "00:00" é texto e aparece. Sem hora nenhuma no lado, nada.
const horariosDoVoo = (saida, chegada) => {
    const s = String(saida ?? '').split('|').map(h => h.trim());
    const c = String(chegada ?? '').split('|').map(h => h.trim());
    let n = Math.max(s.length, c.length);
    while (n > 0 && !s[n - 1] && !c[n - 1]) n--;
    return Array.from({ length: n }, (_, i) => `${s[i] || '--:--'}–${c[i] || '--:--'}`).join(' / ');
};
function Voos({ l }) {
    const trecho = (rotulo, icone, escolha, naoUtiliza) => {
        if (naoUtiliza) return <div className="fin-pp__cell-muted" style={{ whiteSpace: 'nowrap' }}>{icone} {rotulo}: não utilizará</div>;
        if (!escolha) return <div className="fin-pp__cell-muted" style={{ whiteSpace: 'nowrap' }}>{icone} {rotulo}: sem escolha</div>;
        const caminho = [partesDoVoo(escolha.origem)[0], ...partesDoVoo(escolha.destino)].filter(Boolean).map(cidadeDe).join(' ➝ ');
        const dia = (partesDoVoo(escolha.data)[0] ?? '').replace(/^(\d{2}\/\d{2})\/\d{4}$/, '$1');
        const horas = horariosDoVoo(escolha.horaSaida, escolha.horaChegada);
        const completo = [rotulo, escolha.data, escolha.companhia, escolha.voo, `${escolha.origem} ➝ ${escolha.destino}`, horas].filter(Boolean).join(' · ');
        return (
            <div title={completo} style={{ whiteSpace: 'nowrap' }}>
                <span style={{ marginRight: 6 }}>{icone}</span>
                <strong>{dia}</strong> {[...new Set(partesDoVoo(escolha.companhia))].join('/')} {partesDoVoo(escolha.voo).join('/')}
                {horas && <span> · {horas}</span>}
                <span className="fin-pp__cell-muted"> · {caminho}</span>
            </div>
        );
    };
    // No bilhete da Azul o e-ticket é o próprio LOC: sai uma vez só.
    const bilhete = (g) => [g.localizador && `LOC ${g.localizador}`, g.eticket && g.eticket !== g.localizador && eticketFormatado(g.eticket)]
        .filter(Boolean).join(' · ');
    const gi = l.emitidoIda;
    const gr = l.emitidoRetorno;
    const gravados = gi && gr && bilhete(gi) === bilhete(gr)
        ? [bilhete(gi)]
        : [gi && `Ida ${bilhete(gi)}`, gr && `Volta ${bilhete(gr)}`].filter(Boolean);
    return (
        <div>
            {trecho('Ida', <FaPlaneDeparture size={12} />, l.escolhaIda, l.naoUtilizaIda)}
            {trecho('Retorno', <FaPlaneArrival size={12} />, l.escolhaRetorno, l.naoUtilizaRetorno)}
            {gravados.length > 0 && (
                <div className="fin-pp__cell-muted" style={{ fontSize: 11, marginTop: 2, whiteSpace: 'nowrap' }}>Gravado · {gravados.join(' · ')}</div>
            )}
        </div>
    );
}

// O que a gravação do bilhete escreve no cadastro do participante, ao lado da opção que o convidado
// escolheu; no bilhete gravado, o que ela escreveu. Valor que difere da escolha sai em destaque.
// Voo e data diferentes da escolha, pela regra das divergências do back (`divergencias`, com `numerosVoo` e
// `primeiraData`): o último número de cada perna do voo, e a primeira data. É a linha que a revisão marca.
const numerosDoVoo = (t) => String(t ?? '').split(/\s*\|\s*|\n+/)
    .map(parte => parte.match(/\d+/g)).filter(Boolean).map(m => Number(m[m.length - 1]));
const primeiraData = (t) => (String(t ?? '').match(/\d{2}\/\d{2}\/\d{4}/) ?? [null])[0];
const divergeDaEscolha = (c) => {
    if (!String(c.escolha ?? '').trim()) return false;
    if (/voo$/.test(c.campo)) return numerosDoVoo(c.escolha).join(',') !== numerosDoVoo(c.novo).join(',');
    if (/data$/.test(c.campo)) return !!primeiraData(c.escolha) && primeiraData(c.escolha) !== primeiraData(c.novo);
    return false;
};
const MARCA_TEXTO = { background: 'var(--fin-wnb)', borderRadius: 3, padding: '0 4px', boxDecorationBreak: 'clone', WebkitBoxDecorationBreak: 'clone' };

// `marcarDivergentes` (a revisão): a linha do campo divergente da escolha sai marcada, como marca-texto.
export function CamposAGravar({ campos, gravado = false, marcarDivergentes = false }) {
    return (
        <div className="fin-pp__difs">
            <span className="fin-pp__difs-head">Campo</span>
            <span className="fin-pp__difs-head">Escolha do convidado</span>
            <span className="fin-pp__difs-head">{gravado ? 'Gravado' : 'Vai gravar'}</span>
            {campos.map(c => {
                const marcada = marcarDivergentes && divergeDaEscolha(c);
                const celula = (conteudo, className) => (
                    <span className={className}>{marcada ? <span style={MARCA_TEXTO}>{conteudo}</span> : conteudo}</span>
                );
                return (
                    <React.Fragment key={c.campo}>
                        {celula(c.rotulo)}
                        {celula(c.escolha || '—', 'fin-pp__cell-muted')}
                        {celula(c.novo || '—', String(c.escolha ?? '') !== String(c.novo ?? '') ? 'fin-pp__cell-strong' : undefined)}
                    </React.Fragment>
                );
            })}
        </div>
    );
}

const SpinnerBotao = () => (
    <span className="fin-pp__spinner" style={{ width: 11, height: 11, borderWidth: 2, margin: '0 5px 0 0', verticalAlign: '-1px' }} />
);

// O select do status, vestido como o badge do status escolhido: fundo pastel, borda e texto na
// cor adaptada. Na lista, cada opção aparece como badge; no controle, só o texto. Rótulo longo
// quebra linha em vez de ser cortado: o react-select 4 posiciona o valor em absoluto, no meio do
// controle, e o texto de duas linhas vazava para fora da caixa — aqui ele volta ao fluxo, e o
// controle cresce com ele.
// Sem acento e em minúscula, para a busca do select achar "alteracao" em "ALTERAÇÃO".
const semAcento = (texto) => String(texto ?? '').normalize('NFD').replace(/\p{M}/gu, '').toLowerCase();

// As opções são a legenda inteira, em toda linha; digitando, a busca filtra. A opção travada leva o
// `motivo`, que o menu mostra ao lado dela.
function SelectStatus({ rotulo, legenda, opcoes, onEscolher }) {
    const { tinta, traco } = corDaLegenda(rotulo, legenda);
    // O status atual pode não estar entre as opções, numa combinação fora da legenda. Ele entra só para
    // ser EXIBIDO — o select mostra apenas valor que está nas opções, e a linha aparecia com o select em
    // branco — e o filtro o tira do menu.
    const todas = opcoes.some(o => o.value === rotulo) ? opcoes : [...opcoes, { value: rotulo, label: rotulo, soExibe: true }];
    return (
        <ReactSelect compact placeholder="Status" options={todas}
            value={{ value: rotulo, label: rotulo }}
            filterOption={(o, digitado) => {
                if (o.data?.soExibe) return false;
                const termo = semAcento(digitado).trim();
                return !termo || semAcento(o.label).includes(termo);
            }}
            formatOptionLabel={(o, meta) => (meta?.context === 'menu' ? (
                <span className="d-inline-flex align-items-center gap-2 flex-wrap">
                    <PillLegenda rotulo={o.value} legenda={legenda} />
                    {o.motivo && <span className="fin-pp__cell-muted" style={{ fontSize: 11 }}>{o.motivo}</span>}
                </span>
            ) : o.label)}
            onChange={o => { if (o) onEscolher(o.value); }}
            styles={{
                control: (base) => ({ ...base, height: 'auto', minHeight: 36, backgroundColor: tinta + '1f', borderColor: traco + '55',
                    '&:hover': { borderColor: traco } }),
                singleValue: (base) => ({ ...base, position: 'static', top: 'auto', transform: 'none', flex: '1 1 auto', minWidth: 0,
                    maxWidth: '100%', overflow: 'visible', color: traco, fontSize: 11, fontWeight: 600, whiteSpace: 'normal', lineHeight: 1.3, padding: '5px 0' }),
                valueContainer: (base) => ({ ...base, paddingLeft: 10, paddingRight: 4, flexWrap: 'nowrap' }),
                dropdownIndicator: (base) => ({ ...base, color: traco }),
                indicatorSeparator: (base) => ({ ...base, backgroundColor: traco + '55' }),
                option: (base, state) => ({ ...base, padding: '6px 10px', opacity: state.isDisabled ? 0.45 : 1 }),
            }} />
    );
}

/**
 * `modo`: 'rsvp' (só leitura, seleção de participantes para solicitar) ou 'aereo' (escolhe
 * status e observação; bilhete não se seleciona, todo pronto vai).
 * `selecao` (só no RSVP): { marcados: Set, alternar(id), marcarVarios(ids, marcado), selecionavel(l) }.
 * `rascunhos`: { [idEmissao]: { rotulo?, observacao?, reembolsoPrevisto?, tarifaPrevistaIda? e afins (`CAMPOS_PREVISTOS`,
 * do motivo), feeCliente? (aba FEE do lápis), comunicacaoIntermediaria?, assentosIda?, assentosVolta?, conferir? } } — o
 * que o Aéreo mudou na tela e ainda não salvou (as quatro últimas vêm do lápis); `feePadrao`: o FEE por bilhete padrão
 * do evento, que vale na linha cujo rascunho tirou o FEE dela (`valorNaTela`);
 * `onRascunho(idEmissao, campos)` e `onStatus(linha, rotulo, { emitidoAutomatico })` alimentam esse rascunho —
 * `emitidoAutomatico`: o Emitido escolhido é o que a gravação já ia dar à linha.
 * `observacoesRsvp`: { [idParticipante]: texto } — o que o RSVP escreve para o Aéreo na linha
 * que pode ir a ele, antes de solicitar; `onObservacaoRsvp(idParticipante, texto)` alimenta.
 * `onCancelar(linha, { rotulo, descricao })`: o botão Cancelar da linha, nas duas telas. Cada tela
 * guarda no rascunho dela; `cancelamentos` ({ [idParticipante]: { descricao } }) é o do RSVP, que
 * não escolhe tipo nem muda o status: o cancelamento dele só marca a linha como "Cancelado pelo
 * RSVP" (`canceladoRsvp` na entrada), e o tipo do cancelamento quem dá é o Aéreo.
 * `onReenviar(linha, { observacao } | null)`: o Reenviar da linha, só no RSVP; `reenvios` ({ [idParticipante]:
 * { observacao } | null }) é o que a tela mudou nele, e null tira o que já estava salvo. No Solicitar, a linha
 * volta ao Aéreo na mesma linha, em A EMITIR. No `cancelamentos` do RSVP, null também tira o salvo: é o
 * "Tirar o cancelamento" do modal, que chama `onCancelar(linha, null)`.
 * `onExcluirObservacao(linha, anotacao)`: o X da anotação que ainda está no rascunho do lado da tela, que
 * marca ou desmarca a exclusão; nada vai ao back antes do Salvar. As marcadas ({ indice, texto }) vêm do
 * rascunho da linha no Aéreo (`excluirObservacoes`) e de `observacoesExcluidas` ({ [idParticipante]: [...] })
 * no RSVP.
 * Não há seleção de bilhete: todo bilhete pronto (`prontoParaGravar`) vai no próximo envio do
 * Aéreo. O RSVP não escolhe status — ele pede a emissão, sempre em A EMITIR.
 * `onColunas(linha)` (só no Aéreo): o lápis da linha, que abre o modal das colunas editáveis — a
 * Comunicação Intermediária e o DBL CHECK. Nas duas telas, as conferências aparecem ao lado do primeiro
 * bilhete, e a comunicação, na coluna de observação.
 * A `linha` de `onStatus`, `onCancelar` e `onColunas` é o participante com `emissao` = a entrada
 * da linha clicada: no Aéreo, a anterior também se edita, e a página grava em `linha.emissao.id`.
 * `linhas`: na ordem do back, cada participante com `numero`, `grupo` (a requisição da primeira
 * entrada), `emissao` (a atual) e `anteriores`.
 * `filtroOrigem`: a cidade de onde o participante sai (`origemDe`), ou `SEM_ORIGEM`.
 * `filtroVoo` (só no Aéreo): { cia, voo, data } — fica quem tem o voo na linha atual (`voosAtuais`).
 * `filtroAba`: a aba do rodapé escolhida (`AbasDaPlanilha`), { ano, tipos }, ou null para a Todos Participantes.
 * `onDblCheck(linhas)` (só no Aéreo): o DBL CHK em massa do cabeçalho dos bilhetes, que se habilita com o
 * filtro de voo. Recebe os participantes à vista cuja linha atual quem está na tela opera e tem bilhete
 * vinculado; a página abre o modal deles.
 * `onRevisar(bilhete)` (só no Aéreo): o clique na parte de cima do bilhete (selo, trecho, número, situação e
 * "Informações divergentes"), que abre a revisão do participante dele na página.
 * No Aéreo: `podeEditar(l)` diz se a linha do participante se opera nesta tela — é de quem o atende; as
 * outras ficam só leitura, como na tela do RSVP. `daPessoa(l)` diz se ela é de quem está na tela, em
 * qualquer aba: é por ele, e não pela aba, que os textos dizem quem a manda ao RSVP (o Enviar desta
 * tela ou o de quem atende o participante). `selecao` (mesma forma da do RSVP) põe a coluna de
 * marcar, para assumir ou desvincular os participantes, e `mostrarAtendente` diz quem atende cada um.
 * `mensagemVazia`: o que a tabela sem linha diz, no lugar do "nenhum participante neste filtro".
 */
export default function PlanilhaEmissaoAereo({
    linhas, legenda, modo, filtro, filtroRequisicao, filtroOrigem, filtroVoo, filtroAba, busca, selecao, processando, baixando, rascunhos,
    onRascunho, onStatus, onBaixarEditada, onBaixarOriginal, onVincular, onExcluir, podeOriginal,
    observacoesRsvp, onObservacaoRsvp, cancelamentos, onCancelar, reenvios, onReenviar, onColunas, onExcluirObservacao, observacoesExcluidas,
    podeEditar, daPessoa, mostrarAtendente, mensagemVazia, onDblCheck, onRevisar, feePadrao = null,
}) {
    const aereo = modo === 'aereo';
    // O cargo de quem está na tela: a observação dele se escreve no popover; a do outro, só se lê.
    const cargo = aereo ? 'Aéreo' : 'RSVP';
    const [cancelando, setCancelando] = useState(null); // { linha, rotulo, descricao } no modal do Cancelar
    const [reenviando, setReenviando] = useState(null); // { linha, observacao, temRascunho } no modal do Reenviar
    // Os "campos a gravar" abertos, pelo id do bilhete.
    const [abertos, setAbertos] = useState(new Set());
    const alternarAberto = (id) => setAbertos(prev => {
        const novo = new Set(prev);
        if (novo.has(id)) novo.delete(id); else novo.add(id);
        return novo;
    });

    // Filtro e busca mostram o bloco inteiro do participante que casa em alguma linha, na ordem do back;
    // a faixa do grupo só aparece com bloco à vista nele. O status filtrado é o que a linha mostra, com o
    // rascunho (`rotulosDasLinhas`): o escolhido e o Emitido que o envio vai dar já se filtram.
    // A seleção do RSVP: quem está marcado e abre linha nova aparece em A EMITIR (`comPedidoNaTela`).
    const marcados = !aereo && selecao ? selecao.marcados : null;
    const visiveis = useMemo(() => {
        const tela = { aereo, rascunhos, cancelamentos, marcados };
        const filtros = { status: filtro, busca, origem: filtroOrigem, requisicao: filtroRequisicao, voo: filtroVoo, aba: filtroAba };
        return (linhas ?? []).filter(l => passaNosFiltros(l, filtros, tela));
    }, [linhas, filtro, filtroRequisicao, filtroOrigem, filtroVoo, filtroAba, busca, aereo, rascunhos, cancelamentos, marcados]);

    // Os cancelamentos da legenda: o Aéreo escolhe o tipo, que é o que o contador da planilha conta.
    const opcoesCancelamento = useMemo(() => Object.keys(legenda ?? {}).filter(ehCancelamento)
        .map(r => ({ value: r, label: r })), [legenda]);
    // O select do status oferece a legenda inteira em toda linha, com os cancelamentos por último: o
    // cancelamento escolhido nele abre o modal do Cancelar, que pede a descrição. O "Emitido" se habilita
    // na linha que tem bilhete, inclusive a cancelada — ali é a escolha do Aéreo, e o Enviar grava o
    // bilhete dela —, e na linha em que os bilhetes que vão ser gravados fecham a emissão: ali ele é o que
    // a gravação dá, e escolhê-lo é voltar ao automático.
    const opcoesStatus = useMemo(() => [
        ...Object.keys(legenda ?? {}).filter(r => !ehCancelamento(r)).map(r => ({ value: r, label: r })),
        ...opcoesCancelamento,
    ], [legenda, opcoesCancelamento]);

    // O DBL CHK em massa: os participantes à vista com a linha atual operável por quem está na tela e com
    // bilhete vinculado nela — a conferência é do bilhete. Só com um voo filtrado, que é o que ela confere.
    const vooFiltrado = filtroVooAtivo(filtroVoo);
    const operaveis = aereo && onDblCheck ? visiveis.filter(l => !!l.emissao && (!podeEditar || podeEditar(l))) : [];
    const conferiveis = operaveis.filter(l => (l.emissao.bilhetes ?? []).length > 0);
    const podeConferirVoo = vooFiltrado && conferiveis.length > 0 && !processando;

    const selecionaveis = selecao ? visiveis.filter(l => selecao.selecionavel(l)) : [];
    const todosMarcados = selecionaveis.length > 0 && selecionaveis.every(l => selecao.marcados.has(l.id));
    // A coluna de marcar: sempre no RSVP (vazia para quem não solicita); no Aéreo, só com seleção.
    const colunaSelecao = !aereo || !!selecao;

    // `semGravar`: por que o bilhete da linha não grava agora (a linha está cancelada); null quando grava.
    // `cancelada`: a linha está cancelada na tela (`canceladaNaTela`).
    // `extra`: fecha a fila de botões do bilhete (o DBL CHECK da linha, no primeiro bilhete).
    // `acoes`: a linha se opera nesta tela; sem isso, o bilhete só se confere e se baixa. `minha`: a linha
    // é de quem está na tela, em qualquer aba — o Enviar dela é que grava o bilhete pronto. `substituido`: um
    // gravado depois na mesma linha tomou o lugar dele (`bilhetesSubstituidos`).
    const bilhete = (b, semGravar = null, cancelada = false, extra = null, acoes = true, minha = true, substituido = false, onConferir = null) => {
        const d = b.dados ?? {};
        const [rotulo, cor] = SITUACAO_BILHETE[b.situacao] ?? [b.situacao, 'is-muted'];
        const div = b.divergencias ?? [];
        const aberto = abertos.has(b.id) && (b.aplicar ?? []).length > 0;
        const gravado = b.situacao === 'gravado';
        const pendente = ['pendente', 'duplicado'].includes(b.situacao);
        const podeGravar = aereo && prontoParaGravar(b, cancelada);
        // Na linha anterior aberta, o bilhete com trecho que não vale mais na entrega (`naEntrega`) também
        // não vai no envio: o lado dele foi cancelado numa entrada posterior. O da linha com o voo trocado
        // depois da requisição espera o RSVP (`aguardaRsvp`).
        const naoVai = semGravar
            ?? (b.aguardaRsvp && b.trecho ? 'O convidado trocou o voo depois da requisição: este bilhete não vai no Enviar e espera o RSVP reenviar a linha.' : null)
            ?? (!b.atual && !b.naEntrega && b.trecho ? 'Lado cancelado numa requisição posterior: este bilhete não vale mais.' : null);
        // O selo do bilhete: "Pronto para emissão" no que o próximo Enviar grava, "Emitido" no gravado. Os dois
        // dispensam a situação Pendente e Gravado: dela só aparece o Duplicado, que é aviso.
        const emitido = aereo && gravado && !substituido;
        const mostraSituacao = ['duplicado', 'nao_lido'].includes(b.situacao);
        // A parte de cima do bilhete (selo, trecho, número, situação e divergências) abre a revisão do participante,
        // como a observação abre o popover: o texto sublinha no hover da linha. A fileira de botões embaixo faz
        // outras coisas, e não abre.
        const revisavel = aereo && !!onRevisar;
        const abreRevisao = revisavel ? {
            role: 'button', tabIndex: 0, title: 'Abrir a revisão do bilhete', style: { cursor: 'pointer' },
            onClick: () => onRevisar(b),
            onKeyDown: (ev) => { if (ev.key === 'Enter' || ev.key === ' ') { ev.preventDefault(); onRevisar(b); } },
        } : {};
        const sublinha = revisavel ? ' fin-pp__obs-linha fin-pp__obs-celula' : '';
        return (
            <div key={b.id} style={{ padding: '6px 0', borderTop: '1px solid var(--fin-bd)', opacity: (b.atual || b.naEntrega) && !substituido ? 1 : 0.7 }}>
                <div {...abreRevisao}>
                    <div className="d-flex align-items-center gap-2 flex-wrap">
                        {/* Não há o que marcar: todo bilhete pronto é gravado no envio. A pill diz que este é
                            um. Verde, com o bilhete no lugar do ponto; o gravado leva o "Emitido" no mesmo lugar. */}
                        {podeGravar && (
                            <span className="fin-pp__pill fin-pp__pill--plain is-ok" style={{ whiteSpace: 'nowrap' }}
                                title={minha
                                    ? 'Pronto: no Enviar para o RSVP, no rodapé, ele é gravado no cadastro do participante.'
                                    : 'Pronto: é gravado no cadastro do participante no Enviar para o RSVP de quem o atende.'}>
                                <FaTicketAlt size={11} /> Pronto para emissão
                            </span>
                        )}
                        {emitido && (
                            <span className="fin-pp__pill fin-pp__pill--plain is-ok" style={{ whiteSpace: 'nowrap' }}
                                title="Emitido: o bilhete está gravado no cadastro do participante.">
                                <FaTicketAlt size={11} /> Emitido
                            </span>
                        )}
                        {/* Nas duas telas: é o que diz que este não vale mais, e qual vale é o de cima. */}
                        {substituido && (
                            <span className="fin-pp__pill is-muted" style={{ whiteSpace: 'nowrap' }}
                                title="Substituído por um bilhete gravado depois nesta linha: o cadastro guarda o mais novo.">Substituído</span>
                        )}
                        <span className={'fin-pp__cell-strong' + sublinha}>{ROTULO_TRECHO[b.trecho] ?? 'Trecho não identificado'}</span>
                        <span className={'fin-pp__cell-muted' + sublinha}>
                            {d.eticket ? `E-ticket ${eticketFormatado(d.eticket)}` : 'Sem número'}{d.localizador ? ` · LOC ${d.localizador}` : ''}
                        </span>
                        {/* No RSVP todo bilhete que chega é gravado: a pill diria sempre o mesmo. */}
                        {aereo && mostraSituacao && <span className={'fin-pp__pill ' + cor}>{rotulo}</span>}
                    </div>
                    {/* As divergências num badge só, com a lista no hover. */}
                    {aereo && div.length > 0 && (
                        <div style={{ marginTop: 3 }}>
                            <span className="fin-pp__pill fin-pp__pill--plain is-warn" title={div.join('\n')}>
                                <FaExclamationTriangle size={10} /> Informações divergentes ({div.length})
                            </span>
                        </div>
                    )}
                </div>
                <div className="d-flex align-items-center gap-2 flex-wrap" style={{ marginTop: 4 }}>
                    {b.situacao !== 'nao_lido' && (
                        <button type="button" className="fin-pp__btn fin-pp__btn--sm" title={aereo ? 'Cópia editada, sem os valores faturados' : 'Cópia sem nenhum valor'}
                            disabled={baixando === 'editada:' + b.id}
                            onClick={() => onBaixarEditada(b)}>
                            {baixando === 'editada:' + b.id ? <SpinnerBotao /> : <FaFilePdf size={11} style={{ marginRight: 4 }} />} Editada
                        </button>
                    )}
                    {aereo && podeOriginal && (
                        <button type="button" className="fin-pp__btn fin-pp__btn--sm" title="Versão original, com dados sigilosos" onClick={() => onBaixarOriginal(b)}>
                            <FaLock size={10} style={{ marginRight: 4 }} /> Original
                        </button>
                    )}
                    {/* O DBL CHK do bilhete: abre o lápis da linha já na aba dele. No pendente, o lugar é do Trecho. */}
                    {onConferir && !(acoes && ['pendente', 'duplicado'].includes(b.situacao)) && (
                        <button type="button" className="fin-pp__btn fin-pp__btn--sm" disabled={processando}
                            title="DBL CHECK do bilhete" aria-label="DBL CHECK do bilhete" onClick={onConferir}>
                            <FaCheckDouble size={11} />
                        </button>
                    )}
                    {/* Sem trecho o bilhete não é gravado: o botão fica azul para puxar o clique. */}
                    {aereo && acoes && ['pendente', 'duplicado'].includes(b.situacao) && (
                        <button type="button" className={'fin-pp__btn fin-pp__btn--sm' + (b.trecho ? '' : ' fin-pp__btn--primary')}
                            title={b.trecho ? 'Trocar o trecho (ou o participante) do bilhete' : 'Escolha o trecho: sem ele o bilhete não é gravado'}
                            onClick={() => onVincular(b)}>
                            <FaLink size={10} style={{ marginRight: 4 }} /> Trecho
                        </button>
                    )}
                    {aereo && acoes && !gravado && (
                        <button type="button" className="fin-pp__btn fin-pp__btn--sm fin-pp__btn--danger-ghost" title="Excluir bilhete" onClick={() => onExcluir(b)}>
                            <FaTrash size={11} />
                        </button>
                    )}
                    {aereo && (b.aplicar ?? []).length > 0 && (
                        <button type="button" className="fin-pp__linkbtn" onClick={() => alternarAberto(b.id)}>
                            {aberto ? <FaChevronDown size={10} /> : <FaChevronRight size={10} />} {gravado ? 'Campos gravados' : 'Campos a gravar'}
                        </button>
                    )}
                    {extra}
                </div>
                {aberto && <div style={{ marginTop: 6 }}><CamposAGravar campos={b.aplicar} gravado={gravado} /></div>}
                {/* Por que este bilhete não vai no envio: sem isto ele fica pendente sem explicação. */}
                {aereo && naoVai && pendente && (
                    <div style={{ marginTop: 4 }}>
                        <span className="fin-pp__pill is-warn" style={{ whiteSpace: 'normal' }}>{naoVai}</span>
                    </div>
                )}
                {b.motivo && (
                    <div className="d-flex align-items-center gap-2 flex-wrap" style={{ marginTop: 4 }}>
                        <span className="fin-pp__cell-muted" style={{ fontSize: 11 }}>{b.motivo}</span>
                    </div>
                )}
            </div>
        );
    };

    const totalColunas = (colunaSelecao ? 1 : 0) + 4 + 1 + 2;
    // As linhas da tabela, na ordem do back (`ordemDaPlanilha`): a faixa da requisição quando o grupo
    // muda — antes de todo grupo, o da 1ª também; quem ainda não tem entrada fica sob "Não solicitados"
    // — e uma linha por entrada do participante, a anterior logo acima da atual. O Nº vem do back e não
    // se renumera com filtro ou busca: é o mesmo da planilha exportada.
    const linhasDaTabela = [];
    visiveis.forEach((l, i) => {
        if (i === 0 || (visiveis[i - 1].grupo?.id ?? null) !== (l.grupo?.id ?? null)) {
            linhasDaTabela.push({ faixa: true, grupo: l.grupo ?? null });
        }
        const entradas = l.emissao ? entradasDe(l) : [null];
        entradas.forEach((e, k) => linhasDaTabela.push({ l, e, primeira: k === 0, ehAtual: k === entradas.length - 1 }));
    });
    // Só o Aéreo informa o reembolso previsto; o RSVP cancela com reembolso só pelo status e a descrição.
    const pedeReembolso = aereo && ehReembolso(cancelando?.rotulo);
    // Alguma linha à vista com a Comunicação Intermediária: o cabeçalho da observação diz que ela vem junto.
    const temComunicacao = linhasDaTabela.some(it => !it.faixa && !!it.e && comunicacaoNaTela(it.e, rascunhos?.[it.e.id]) !== '');

    return (
        <div className="fin-pp__tblwrap">
            <table className="fin-pp__tbl" style={{ minWidth: aereo ? 1400 : 1200, tableLayout: 'auto' }}>
                <thead>
                    <tr>
                        {colunaSelecao && (
                            <th style={{ width: 36 }}>
                                {selecao && selecionaveis.length > 0 && (
                                    <FinCheck title="Selecionar todos" checked={todosMarcados}
                                        onChange={() => selecao.marcarVarios(selecionaveis.map(l => l.id), !todosMarcados)} />
                                )}
                            </th>
                        )}
                        <th style={{ width: 40 }}>Nº</th>
                        {/* Largura do conteúdo: `1%` faz a coluna encolher até ele, e a sobra da tabela vai
                            para as outras. No Aéreo, o select segura a largura mínima dele. */}
                        <th style={{ width: '1%', whiteSpace: 'nowrap' }}>Status</th>
                        <th>Nome do convidado</th>
                        <th>Voos</th>
                        <th className="text-end" title="Total com FEE, lido dos bilhetes">Valor</th>
                        <th>
                            <span className="d-inline-flex align-items-center gap-1">
                                {temComunicacao ? 'Observação / Comunicação' : 'Observação'}
                                {/* O olho diz que a observação abre: passe o mouse na linha e clique nela. */}
                                <FaEye size={11} style={{ color: '#9fb3d4' }} title="Passe o mouse na linha e clique na observação para ler todas as da linha, inteiras, com quem e quando." />
                            </span>
                        </th>
                        <th style={{ minWidth: 320 }}>
                            {/* No Aéreo, o DBL CHK em massa no meio do que sobra do cabeçalho: sem voo filtrado,
                                desabilitado, e o title diz por quê. */}
                            <div className="d-flex align-items-center">
                                <span>Bilhetes</span>
                                {aereo && onDblCheck && (
                                    <span className="flex-grow-1 d-flex justify-content-center">
                                        <button type="button" disabled={!podeConferirVoo} onClick={() => onDblCheck(conferiveis)}
                                            className={'fin-pp__btn fin-pp__btn--sm fin-pp__btn--pill' + (podeConferirVoo ? ' fin-pp__btn--primary' : '')}
                                            style={{ padding: '4px 12px', letterSpacing: 'normal' }}
                                            title={!vooFiltrado ? 'Filtre por voo (Filtrar voo, nos filtros acima) para conferir em massa.'
                                                : !operaveis.length ? 'Nenhuma linha do filtro é sua: o DBL CHK em massa vale nas linhas que você opera, em Minhas emissões.'
                                                : !conferiveis.length ? 'Nenhuma linha do filtro tem bilhete vinculado: o DBL CHK confere o bilhete.'
                                                : `Conferir as ${conferiveis.length} linhas do voo filtrado que têm bilhete.`}>
                                            <FaCheckDouble size={11} style={{ marginRight: 5 }} /> DBL CHK{vooFiltrado ? ` ${conferiveis.length}` : ''}
                                        </button>
                                    </span>
                                )}
                            </div>
                        </th>
                    </tr>
                </thead>
                <tbody>
                    {visiveis.length === 0 && (
                        <tr><td colSpan={totalColunas} className="fin-pp__empty">
                            {mensagemVazia || (aereo ? 'Nenhum participante na emissão neste filtro.' : 'Nenhum participante com aéreo solicitado neste filtro.')}
                        </td></tr>
                    )}
                    {linhasDaTabela.map((item) => {
                        if (item.faixa) {
                            return (
                                <tr key={'req:' + (item.grupo?.id ?? 'nao-solicitados')} className="fin-pp__tbl-sub">
                                    <td colSpan={totalColunas}>
                                        <span className="fin-pp__sub-name">
                                            {item.grupo
                                                ? `${item.grupo.ordinal} Requisição ${diaMes(item.grupo.solicitadoEm)}${item.grupo.solicitadoPor ? ` · ${item.grupo.solicitadoPor}` : ''}`
                                                : 'Não solicitados'}
                                        </span>
                                    </td>
                                </tr>
                            );
                        }
                        // `e`: a entrada desta linha (null para quem ainda não tem). A atual (`ehAtual`, a
                        // última do participante) leva a lógica de ação; a anterior mostra a requisição, os
                        // bilhetes e os valores DELA. No Aéreo a anterior se edita como a atual (status,
                        // Cancelar, Obs. Aéreo, DBL CHECK): as ações recebem `alvo`, o participante com a
                        // entrada da linha clicada, e a página grava nela. No RSVP, a anterior é só leitura.
                        const { l, e, primeira, ehAtual } = item;
                        const alvo = { ...l, emissao: e };
                        const rascunho = (e && rascunhos?.[e.id]) || null;
                        // Só os bilhetes da própria entrada. Os que valem ficam à vista; os substituídos por um gravado
                        // depois na mesma linha, atrás do "Mostrar substituídos".
                        const bilhetes = e?.bilhetes ?? [];
                        const substituidos = bilhetesSubstituidos(bilhetes);
                        const valendo = bilhetes.filter(b => !substituidos.has(b.id));
                        const verSubstituidos = !!e && abertos.has('substituidos:' + e.id);
                        const emitida = !!e?.emitida;
                        // No Aéreo, a linha se opera só por quem atende o participante (`podeEditar`, da
                        // página); a dos outros fica só leitura, como na tela do RSVP.
                        const operavel = aereo && !!e && (!podeEditar || podeEditar(l));
                        // A linha é de quem está na tela, em qualquer aba: os textos de quem a manda ao RSVP seguem isto.
                        const minha = aereo && !!e && (!daPessoa || daPessoa(l));
                        // Os bilhetes que vão ser gravados neste envio — todo bilhete pronto, sem seleção, pela
                        // situação da linha DELE: a linha anterior aberta também recebe. `prontosAteALinha`: os
                        // desta linha e os das anteriores do participante, que o cancelamento dela derruba (o
                        // lado cancelado deixa de valer nela e nas anteriores). Se a atual vai como emitida
                        // neste envio, quem diz é o `vaiEmitirNaTela`.
                        const prontosAteALinha = aereo
                            ? entradasDe(l).slice(0, entradasDe(l).indexOf(e) + 1).flatMap(x => (x.bilhetes ?? []).filter(b => prontoParaGravar(b, canceladaNaTela(x, rascunhos))))
                            : [];
                        const vaiEmitir = vaiEmitirNaTela(l, e, aereo, rascunhos);
                        // A própria linha tem bilhete (pendente, duplicado ou gravado): o Emitido se escolhe,
                        // inclusive na cancelada, e o Enviar grava o bilhete dela.
                        const temBilheteNaLinha = bilhetes.some(b => ['pendente', 'duplicado', 'gravado'].includes(b.situacao));
                        // O status e a observação mostrados são os do rascunho, quando há.
                        // No RSVP, o cancelamento em rascunho não muda o status: a linha atual mostra o badge
                        // "Cancelamento de emissão solicitado" até o envio, como mostra depois dele.
                        const cancelamento = !aereo && ehAtual ? (cancelamentos?.[l.id] ?? null) : null;
                        // O que o RSVP já salvou na linha e ainda não solicitou (`rsvpPendente`): a linha vem com
                        // ele por cima, e o tipo diz o botão que o envia. O Reenviar tirado na tela (null) já
                        // não conta.
                        const rascunhoRsvp = !aereo && ehAtual ? (e?.rsvpPendente ?? null) : null;
                        // O cancelamento salvo que a tela tirou ("Tirar o cancelamento"): sai no próximo Salvar.
                        const tirouCancelamento = rascunhoRsvp === 'cancelamento' && cancelamentos?.[l.id] === null;
                        const cancelamentoSalvo = rascunhoRsvp === 'cancelamento' && !tirouCancelamento;
                        // O Reenviar salvo, a menos que o cancelamento na tela o substitua: o Salvar grava o
                        // cancelamento no lugar dele. Tirado na tela (null), continua no rascunho até o Salvar, e o
                        // botão segue aberto para pôr de volta.
                        const reenvioNoRascunho = rascunhoRsvp === 'reenvio' && !cancelamento;
                        const reenvioSalvo = reenvioNoRascunho && reenvios?.[l.id] !== null;
                        const canceladoRsvp = (!!e?.canceladoRsvp && !tirouCancelamento) || !!cancelamento;
                        // O pedido novo do RSVP, marcado na tela ou salvo: quem ainda não tem entrada já mostra o A
                        // EMITIR que o envio abre, com o "Não enviado".
                        const comPedido = !aereo && ehAtual && comPedidoNaTela(l, { marcados });
                        // O status da linha, o mesmo que o filtro e a contagem usam (`rotuloNaTela`).
                        const rotulo = rotuloNaTela(l, e, { aereo, rascunhos, marcados });
                        // O valor da linha com a reserva prevista do rascunho da tela (`valorNaTela`).
                        const valorDaLinha = e ? valorNaTela(l, e, { aereo, rascunhos, marcados, feePadrao }) : null;
                        // A linha com rascunho salvo do lado da tela, que o outro lado só vê no envio.
                        const salvaSemEnviar = aereo ? !!e?.aereoPendente : !!rascunhoRsvp;
                        // Cancelar vale para toda linha que está na emissão; no Aéreo, a anterior também. O
                        // RSVP, só na atual e só quem pode enviar. Com bilhete pronto na linha ou numa anterior
                        // do participante, não: ele vai ser gravado neste envio, a linha cancelada não recebe
                        // bilhete e o lado cancelado deixa de valer nas anteriores — quem quer cancelar exclui
                        // o bilhete antes. O da linha posterior não trava. Depois de emitida, cancelar é livre.
                        // A que o Aéreo já cancelou o RSVP não cancela: pede de novo pelo Solicitar Nova Emissão
                        // (`motivoLinhaNaoCancela` no back); o pedido que ficou salvo nela ainda abre, para tirar.
                        const podeCancelar = !!onCancelar && !!e && (aereo ? (operavel && !processando && prontosAteALinha.length === 0)
                            : (!!selecao && ehAtual && (e.situacao !== 'cancelado' || cancelamentoSalvo)));
                        // Já cancelada — pelo status que o Aéreo deu, ou pela marca do RSVP, gravada ou em
                        // rascunho: no RSVP, o Cancelar reabre com a descrição que ela tem.
                        const cancelada = e?.situacao === 'cancelado' || canceladoRsvp;
                        // No RSVP, a linha que o Aéreo cancelou volta a pedir a
                        // emissão pelo Solicitar Nova Emissão: ele marca a linha para a próxima requisição, o mesmo que o
                        // checkbox — os dois são a mesma seleção, e o check no botão acompanha o do checkbox.
                        // Só a que o Aéreo cancelou: com o pedido de cancelamento do RSVP, a linha segue com ele, e o
                        // RSVP a devolve pelo Reenviar.
                        const podeEmitir = !aereo && ehAtual && !!selecao && e?.situacao === 'cancelado'
                            && !cancelamento && selecao.selecionavel(l);
                        const marcadaParaEmitir = podeEmitir && selecao.marcados.has(l.id);
                        // O Reenviar do RSVP em rascunho na linha ({ observacao }): vai no Solicitar Emissão de Aéreo.
                        const reenvio = !aereo && ehAtual ? (reenvios?.[l.id] ?? null) : null;
                        // O Reenviar devolve ao Aéreo a linha que ele não cancelou nem emitiu, na mesma linha, em
                        // A EMITIR, com a observação: a que ele deixou com outro status (as pendências) e a que tem o
                        // voo trocado. O pedido de cancelamento do RSVP não segura, e sai no reenvio. Com o
                        // cancelamento ainda na tela, ou sem escolha de voo (`motivoNaoEnviar`, a etapa resetada),
                        // não. A mesma regra do back (`motivoNaoReenviar`).
                        // O Reenviar já salvo também abre, para editar ou tirar: a linha vem em A EMITIR com ele.
                        const podeReenviar = !aereo && ehAtual && !!selecao && !!onReenviar && !!e && (reenvioNoRascunho
                            || (e.situacao !== 'cancelado' && !cancelamento && !emitida && !l.motivoNaoEnviar
                                && (e.situacao !== 'a_emitir' || !!e.trocouEscolha)));
                        const comReenvio = !!reenvio || reenvioSalvo;
                        // DBL CHECK: as conferências do Aéreo na linha, de nenhuma a N, ao lado do primeiro bilhete
                        // nas duas telas: um check por conferência, que abre a data e quem conferiu no popover da
                        // observação, e um X quando não há nenhuma. O RSVP as vê depois do primeiro envio da linha
                        // pelo Aéreo. Quem marca é o Aéreo, no lápis da linha que ele opera (`onColunas`); a
                        // gravada não sai, e a que está no rascunho do lápis aparece em âmbar, ainda sem data. A salva e
                        // não enviada (`rascunho`, só na tela do Aéreo) também é âmbar, já com a data do Salvar.
                        const conferencias = [...(e?.dblCheck ?? []), ...(rascunho?.conferir ? [{ naoEnviada: true }] : [])];
                        const textoDaConferencia = (c) => (c.naoEnviada ? 'A sua, não salva: grava com a data do Salvar.'
                            : `${dataHora(c.em)}${c.nome ? ` por ${c.nome}` : ''}${c.rascunho ? '\nEnvio pendente' : ''}`);
                        const podeEditarColunas = operavel && !!onColunas;
                        const dblCheck = e ? (
                            <span className="d-inline-flex align-items-center gap-1" style={{ whiteSpace: 'nowrap' }}>
                                <span className="fin-pp__cell-muted" style={{ fontSize: 12 }}>Dbl Chk:</span>
                                {conferencias.length ? conferencias.map((c, n) => (
                                    <IconeInfo key={n} rotulo={`${n + 1}ª conferência do DBL CHECK`}
                                        cor={c.naoEnviada || c.rascunho ? 'var(--fin-wn)' : 'var(--fin-ok)'}
                                        texto={textoDaConferencia(c)}>
                                        <FaCheck size={12} />
                                    </IconeInfo>
                                )) : (
                                    <span className="d-inline-flex" title="Nenhuma conferência ainda"
                                        style={{ color: 'var(--fin-mt)', opacity: 0.6 }}>
                                        <FaTimes size={12} />
                                    </span>
                                )}
                            </span>
                        ) : null;
                        // A linha cancelada recebe bilhete, mas ele só grava quando o Aéreo a marca como Emitido;
                        // a emitida grava o novo no Enviar. O back aplica a mesma regra (`bilhetesParaGravar`).
                        const linhaCancelada = !!e && canceladaNaTela(e, rascunhos);
                        const semGravar = linhaCancelada ? 'Linha cancelada: mude o status para gravar o bilhete.' : null;
                        // A anotação nova do Aéreo no rascunho da tela; sem ela, em branco: o editor escreve uma
                        // anotação nova, e a gravada não se edita.
                        const observacao = rascunho && 'observacao' in rascunho ? rascunho.observacao : '';
                        // O reembolso previsto: o do rascunho do Aéreo, quando há; senão, o gravado.
                        const reembolso = rascunho && 'reembolsoPrevisto' in rascunho ? rascunho.reembolsoPrevisto : (e?.reembolsoPrevisto ?? null);
                        // Os assentos que o Aéreo informa no lápis, do mesmo jeito: os do rascunho, quando há; senão, os gravados.
                        const assento = (campo) => (rascunho && campo in rascunho ? rascunho[campo] : (e?.[campo] ?? null));
                        const editavel = operavel && !processando;
                        // Observação: o Aéreo anota em toda linha que edita. O RSVP, em toda linha que pode ir
                        // ao Aéreo: na que abre entrada nova, a anotação vai com o pedido; na que não abre,
                        // entra na entrada que a linha tem. Com o cancelamento do RSVP em rascunho, a anotação
                        // é a descrição dele, que se edita no Cancelar. Na linha anterior, o RSVP só lê. O
                        // editor escreve uma anotação NOVA: as de antes ficam na lista, com quem e quando.
                        // Com o Reenviar em rascunho, a anotação é a dele, que se edita no Reenviar; com o
                        // cancelamento salvo, a descrição dele, que se edita no Cancelar.
                        const escreve = aereo ? !!editavel
                            : ehAtual && !!onObservacaoRsvp && !!selecao && !cancelamento && !cancelamentoSalvo && !comReenvio
                                && (selecao.marcados.has(l.id) || selecao.selecionavel(l));
                        const propria = aereo ? observacao
                            : cancelamento ? cancelamento.descricao
                            : (escreve ? (observacoesRsvp?.[l.id] ?? '') : '');
                        // O que quem está na tela escreveu e ainda não gravou: no Aéreo, a observação nova do
                        // rascunho; no RSVP, a descrição do cancelamento ou a observação do próximo pedido. A
                        // célula já a mostra, como a mais nova, marcada como não enviada. Igual à mais nova
                        // gravada daquele lado, não é nova (o back não a acrescenta).
                        const naTela = aereo
                            ? (rascunho && 'observacao' in rascunho ? rascunho.observacao : null)
                            : (cancelamento ? cancelamento.descricao : reenvio ? reenvio.observacao : (escreve ? (observacoesRsvp?.[l.id] ?? null) : null));
                        const textoNaTela = String(naTela ?? '').trim();
                        const mudouNaTela = naTela !== null && textoNaTela !== textoMaisNovo(aereo ? e?.observacao : e?.observacaoRsvp).trim();
                        const pendente = mudouNaTela && textoNaTela !== '' ? { texto: textoNaTela, lado: cargo, naoEnviada: true } : null;
                        // A Comunicação Intermediária como vai ficar: a do rascunho do lápis por cima da gravada.
                        const comunicacao = e ? comunicacaoNaTela(e, rascunho) : '';
                        const temAnotacoes = !!pendente || comunicacao !== ''
                            || anotacoesDe(e?.observacaoRsvp).length + anotacoesDe(e?.observacao).length > 0;
                        return (
                            <tr key={e?.id ?? l.id} style={ehAtual && e?.trocouEscolha ? { background: 'var(--fin-wnb)' } : undefined}>
                                {colunaSelecao && (
                                    <td>
                                        {/* A seleção é do participante: o checkbox vai só na linha atual. */}
                                        {selecao && ehAtual && (
                                            <span title={l.motivoNaoEnviar || undefined}>
                                                <FinCheck checked={selecao.marcados.has(l.id)} disabled={!selecao.selecionavel(l)} onChange={() => selecao.alternar(l.id)} />
                                            </span>
                                        )}
                                    </td>
                                )}
                                {/* O Nº conta participantes: só a primeira linha dele o leva. As seguintes, que a
                                    faixa deixa sob a requisição em que ele entrou, dizem a requisição delas, com a data e
                                    quem pediu no hover. A que o Aéreo abriu no Vincular Cancelado (`abertaPeloAereo`) fica vazia:
                                    não veio de pedido do RSVP. */}
                                <td className="fin-pp__cell-muted" style={primeira ? undefined : { whiteSpace: 'nowrap' }}
                                    title={!primeira && e?.requisicao && !e.abertaPeloAereo
                                        ? `${e.requisicao.ordinal} requisição · ${dataHora(e.requisicao.solicitadoEm)}${e.requisicao.solicitadoPor ? ` · ${e.requisicao.solicitadoPor}` : ''}`
                                        : undefined}>
                                    {primeira ? (l.numero ?? '') : (e?.requisicao && !e.abertaPeloAereo ? `${e.requisicao.ordinal} Req.` : '')}
                                </td>
                                <td style={aereo ? undefined : { whiteSpace: 'nowrap' }}>
                                    {/* O select fica também na linha cancelada. O cancelamento escolhido nele abre o
                                        modal do Cancelar, com a mesma trava do botão: sem descrição não cancela, e
                                        com bilhete pronto na linha ou numa anterior não se cancela (o Enviar recusa). */}
                                    {editavel ? (
                                        <span style={{ display: 'block', minWidth: 230 }}>
                                            <SelectStatus rotulo={rotulo} legenda={legenda}
                                                opcoes={opcoesStatus.map(o => (o.value === 'EMITIDO' && !vaiEmitir && !temBilheteNaLinha
                                                    ? { ...o, disabled: true, motivo: 'Suba ou vincule um bilhete na linha' }
                                                    : ehCancelamento(o.value) && !podeCancelar
                                                        ? { ...o, disabled: true, motivo: prontosAteALinha.length ? 'Exclua o bilhete pronto antes de cancelar' : undefined }
                                                        : o))}
                                                onEscolher={valor => {
                                                    if (valor === rotulo) return;
                                                    // A observação da tela vale entre os status até o Salvar: o
                                                    // cancelamento abre com ela, como o motivo dos outros status.
                                                    if (ehCancelamento(valor)) {
                                                        setCancelando({ linha: alvo, rotulo: valor, descricao: observacao,
                                                            reembolso: ehReembolso(valor) ? reembolso : null });
                                                        return;
                                                    }
                                                    onStatus(alvo, valor, { emitidoAutomatico: vaiEmitir });
                                                }} />
                                        </span>
                                    ) : (
                                        <span style={{ display: 'block', minWidth: 230 }}>
                                            {/* Na largura do select das linhas editáveis, e quebra como ele: sem a largura
                                                mínima, a coluna encolhe até o conteúdo e o badge vira uma palavra por linha. */}
                                            <PillLegenda rotulo={rotulo} legenda={legenda} />
                                        </span>
                                    )}
                                    {/* O SLA da Sanofi (`slaDaLinha` no back): só a linha fora do prazo ganha a marca, com os
                                        números no passar do mouse; a que está no prazo não mostra nada. */}
                                    {foraDoSla(e?.sla) && (
                                        <div style={{ marginTop: 4 }}>
                                            <span className="fin-pp__pill is-danger" style={{ whiteSpace: 'nowrap' }} title={textoDoSla(e.sla)}>Fora do SLA</span>
                                        </div>
                                    )}
                                    {(rascunho || vaiEmitir || canceladoRsvp || salvaSemEnviar || comPedido || comReenvio) && (
                                        <div className="d-flex align-items-center gap-1 flex-wrap" style={{ marginTop: 4 }}>
                                            {/* Sem quebra: entra na largura da coluna, que encolhe até o conteúdo. */}
                                            {(rascunho || vaiEmitir || cancelamento || salvaSemEnviar || comPedido || comReenvio) && (
                                                <span className="fin-pp__pill is-warn" style={{ whiteSpace: 'nowrap' }}
                                                    title={aereo
                                                        ? (!minha ? 'Rascunho salvo por quem atende o participante: o RSVP o vê no Enviar dessa pessoa.'
                                                            : rascunho ? 'Não salvo. O Salvar guarda sem mandar ao RSVP; o Enviar para o RSVP, no rodapé, manda com o e-mail.'
                                                                : 'Salvo e não enviado: o RSVP vê no Enviar para o RSVP, no rodapé.')
                                                        : (cancelamento || cancelamentoSalvo) ? 'Vai ao Aéreo no Solicitar Cancelamento de Emissão, no rodapé, com o e-mail; o Salvar guarda sem mandar.'
                                                            : comReenvio ? 'Reenvio: vai ao Aéreo no Solicitar Emissão de Aéreo, no rodapé, com o e-mail; o Salvar guarda sem mandar.'
                                                            : comPedido ? (l.pedidoSalvo
                                                                ? 'Pedido de emissão salvo: vai ao Aéreo no Solicitar Emissão de Aéreo, no rodapé.'
                                                                : 'Marcado para a próxima requisição: vai ao Aéreo no Solicitar Emissão de Aéreo, no rodapé; o Salvar guarda sem mandar.')
                                                            : 'Observação salva: vai ao Aéreo no Solicitar Emissão de Aéreo, no rodapé.'}>Não enviado</span>
                                            )}
                                            {/* O status muda só no envio: até lá a linha mostra o que o Aéreo deu. */}
                                            {comReenvio && (
                                                <span className="fin-pp__pill is-info" style={{ whiteSpace: 'nowrap' }}
                                                    title={'No Solicitar Emissão de Aéreo, a linha volta para EMITIR com a sua observação'
                                                        + (e?.trocouEscolha ? ' e com o voo novo do convidado' : '')
                                                        + (e?.canceladoRsvp ? ', sem o pedido de cancelamento.' : '.')}>Reenvio</span>
                                            )}
                                            {/* A marca do RSVP não é status: a linha fica com o que o Aéreo deu. */}
                                            {canceladoRsvp && (
                                                <span className="fin-pp__pill is-danger" style={{ whiteSpace: 'nowrap' }}
                                                    title="O RSVP solicitou o cancelamento da emissão desta linha. O status continua o que o Aéreo deu: o tipo do cancelamento quem escolhe é ele.">Cancelamento de emissão solicitado</span>
                                            )}
                                        </div>
                                    )}
                                    {(podeCancelar || podeEmitir || podeReenviar || podeEditarColunas) && (
                                        <div className="d-flex align-items-center gap-2" style={{ marginTop: 6 }}>
                                            {/* Com o Solicitar Nova Emissão na linha cancelada, o Cancelar sai: ela
                                                já está cancelada, e a ação que sobra é pedir a emissão de novo. No
                                                RSVP, o Cancelar é o Solicitar Cancelamento de Emissão. */}
                                            {podeCancelar && !podeEmitir && (
                                                <button type="button" className="fin-pp__btn fin-pp__btn--sm fin-pp__btn--danger-ghost"
                                                    onClick={() => setCancelando({
                                                        linha: alvo,
                                                        // O cancelamento do RSVP que está na tela ou salvo: o modal oferece tirá-lo.
                                                        temRascunho: !aereo && (!!cancelamento || cancelamentoSalvo),
                                                        // O RSVP não escolhe tipo: ele só descreve, e a linha fica marcada.
                                                        rotulo: aereo ? (ehCancelamento(rotulo) ? rotulo : '') : null,
                                                        // No Aéreo, a observação da tela, que vale entre os status até o Salvar.
                                                        descricao: aereo ? observacao
                                                            : cancelada ? (cancelamento?.descricao ?? textoMaisNovo(e?.observacaoRsvp)) : '',
                                                        reembolso: ehReembolso(rotulo) ? reembolso : null,
                                                    })}>
                                                    {aereo ? 'Cancelar' : 'Solicitar Cancelamento de Emissão'}
                                                </button>
                                            )}
                                            {podeReenviar && (
                                                <button type="button" className="fin-pp__btn fin-pp__btn--sm"
                                                    title={comReenvio
                                                        ? 'Reenvio não enviado: vai ao Aéreo no Solicitar Emissão de Aéreo, no rodapé. Clique para editar ou tirar.'
                                                        : 'Devolver a linha ao Aéreo na mesma linha: volta para EMITIR com a sua observação'
                                                            + (e.trocouEscolha ? ' e com o voo novo do convidado' : '')
                                                            + (e.canceladoRsvp ? ', sem o pedido de cancelamento.' : '.')}
                                                    // O Reenviar salvo reabre com a observação dele, que é a mais nova do RSVP na linha.
                                                    onClick={() => setReenviando({ linha: alvo, temRascunho: comReenvio,
                                                        observacao: reenvio?.observacao ?? (reenvioSalvo ? textoMaisNovo(e?.observacaoRsvp) : (observacoesRsvp?.[l.id] ?? '')) })}>
                                                    <FaRedo size={10} /> Reenviar
                                                    {comReenvio && <FaCheck size={10} style={{ color: 'var(--fin-ok)' }} />}
                                                </button>
                                            )}
                                            {podeEmitir && (
                                                <button type="button" className="fin-pp__btn fin-pp__btn--sm"
                                                    title={marcadaParaEmitir ? 'Vai na próxima requisição: clique para tirar.' : 'Pedir a emissão de novo: a linha vai na próxima requisição.'}
                                                    onClick={() => selecao.alternar(l.id)}>
                                                    <FaTicketAlt size={10} /> Solicitar Nova Emissão
                                                    {marcadaParaEmitir && <FaCheck size={10} style={{ color: 'var(--fin-ok)' }} />}
                                                </button>
                                            )}
                                            {podeEditarColunas && (
                                                <button type="button" className="fin-pp__btn fin-pp__btn--sm" disabled={processando}
                                                    aria-label="Colunas editáveis"
                                                    title="Colunas editáveis: Comunicação Intermediária, Assentos e DBL CHECK. Vão para as colunas delas na planilha de emissão."
                                                    onClick={() => onColunas(alvo)}>
                                                    <FaPencilAlt size={11} />
                                                </button>
                                            )}
                                        </div>
                                    )}
                                </td>
                                <td className="fin-pp__cell-strong" style={{ minWidth: 180 }}>
                                    {/* O nome abre o cadastro (CPF, nascimento, documentos, One Key) no mesmo popover
                                        das observações, para copiar na reserva. Mesmo sublinhado: azul, fino, no hover da linha. */}
                                    <PopoverObservacao rotulo="Dados do participante"
                                        titulo="Clique para ver os dados do cadastro: CPF, nascimento, documentos e One Key"
                                        conteudo={<DadosParticipante participante={l} />}>
                                        <span className="fin-pp__obs-linha fin-pp__obs-celula">{l.nome}</span>
                                    </PopoverObservacao>
                                    {/* Quem do Aéreo atende o participante: nas abas em que a linha pode ser de outra pessoa. */}
                                    {mostrarAtendente && ehAtual && (
                                        <div style={{ marginTop: 6 }}><span className={'fin-pp__pill ' + (l.atendente ? 'is-info' : 'is-muted')}
                                            title={l.atendente ? 'Quem do Aéreo atende este participante' : 'Ninguém do Aéreo atende este participante ainda'}>
                                            {l.atendente ? l.atendente.nome : 'Sem atendente'}
                                        </span></div>
                                    )}
                                </td>
                                {/* A anterior mostra os voos que ELA pediu e o que foi gravado dela (o back manda). A
                                    troca de voo do convidado fica aqui, embaixo dos voos. Para o Aéreo, a atual com a
                                    troca mostra os voos pedidos: o voo novo só chega a ele quando o RSVP reenviar a linha. */}
                                <td>
                                    <Voos l={ehAtual && !(aereo && e?.trocouEscolha) ? l : e} />
                                    {ehAtual && e?.trocouEscolha && (
                                        <div style={{ marginTop: 6 }}><span className="fin-pp__pill is-warn" title={aereo
                                            ? 'O convidado trocou o voo depois da requisição. Aqui estão os voos que o RSVP pediu, e a linha só recebe bilhete quando ele reenviar a linha.'
                                            : l.motivoNaoEnviar ? 'O convidado ainda não escolheu o voo novo: o Reenviar aparece depois da escolha.' : 'Clique em Reenviar: a troca vai ao Aéreo na mesma linha, no Solicitar Emissão de Aéreo.'}>Voo trocado depois da requisição</span></div>
                                    )}
                                </td>
                                <td className="text-end fin-pp__cell-strong" style={{ whiteSpace: 'nowrap' }} title="Total com FEE, lido dos bilhetes, e a reserva prevista do lado ainda sem bilhete">
                                    {e ? brl(valorDaLinha.comFee) : ''}
                                    {/* A reserva que o Aéreo informou no motivo da aprovação de custos, no lado ainda sem
                                        bilhete: entra no total até o bilhete chegar. */}
                                    {[['ida', 'Reserva ida'], ['retorno', 'Reserva volta']].map(([lado, texto]) => (valorDaLinha && valorDaLinha.previstos[lado] !== null && (
                                        <div key={lado} style={{ fontSize: 11, color: 'var(--fin-wn)' }} title="Tarifa e taxas da reserva, informadas pelo Aéreo no motivo da aprovação de custos">
                                            {texto} {brl(valorDaLinha.previstos[lado])}
                                        </div>
                                    )))}
                                    {/* O FEE da Access na linha (por bilhete × NUM TKT), que já está no total acima. */}
                                    {valorDaLinha && Number(valorDaLinha.fee) > 0 && (
                                        <div style={{ fontSize: 11, color: 'var(--fin-mt)' }} title="FEE da Access: o FEE por bilhete da linha, ou o padrão do cliente, × NUM TKT">
                                            FEE {brl(valorDaLinha.fee)}
                                        </div>
                                    )}
                                    {ehReembolso(rotulo) && (
                                        <div style={{ fontSize: 11, color: 'var(--fin-dg)' }} title="Reembolso previsto, informado pelo Aéreo no Cancelar">
                                            Reembolso {brl(reembolso)}
                                        </div>
                                    )}
                                    {/* Os assentos embaixo do total, como o reembolso: só o lado que tem valor. */}
                                    {[['assentosIda', 'Assento ida'], ['assentosVolta', 'Assento volta']].map(([campo, texto]) => (Number(assento(campo)) > 0 && (
                                        <div key={campo} style={{ fontSize: 11, color: 'var(--fin-mt)' }} title="Valor do assento, informado pelo Aéreo no lápis">
                                            {texto} {brl(assento(campo))}
                                        </div>
                                    )))}
                                </td>
                                <td style={{ whiteSpace: 'normal', minWidth: 200 }}>
                                    {/* O excluir da anotação do rascunho: no Aéreo, na linha que ele opera; no RSVP, na
                                        atual, para quem solicita. A marcada sai no Salvar. */}
                                    <Observacoes rsvp={e?.observacaoRsvp} aereo={e?.observacao} pendente={pendente}
                                        comunicacao={comunicacao} comunicacaoNaoEnviada={!!rascunho && 'comunicacaoIntermediaria' in rascunho}
                                        excluidas={aereo ? (rascunho?.excluirObservacoes ?? []) : (observacoesExcluidas?.[l.id] ?? [])}
                                        onExcluir={onExcluirObservacao && (aereo ? editavel : (!!selecao && ehAtual))
                                            ? (nota) => onExcluirObservacao(alvo, nota) : null} />
                                    {escreve ? (
                                        <div style={temAnotacoes ? { marginTop: 6 } : undefined}>
                                            <ObservacaoEditavel texto={propria} rotulo={'Nova observação do ' + cargo}
                                                placeholder={aereo ? (rotulo === 'EMITIDO' ? 'Para o RSVP' : 'Motivo de não emitir') : 'O que mudou, para o Aéreo'}
                                                onSalvar={texto => (aereo ? onRascunho(e.id, { observacao: texto }) : onObservacaoRsvp(l.id, texto))}>
                                                <button type="button" className="fin-pp__btn fin-pp__btn--sm" title={propria || undefined}>
                                                    <FaPen size={10} /> Obs. {cargo}
                                                    {String(propria ?? '').trim() !== '' && <FaCheck size={10} style={{ color: 'var(--fin-ok)' }} />}
                                                </button>
                                            </ObservacaoEditavel>
                                        </div>
                                    ) : (!temAnotacoes && <span className="fin-pp__cell-muted">—</span>)}
                                </td>
                                <td style={{ whiteSpace: 'normal' }}>
                                    {!e && <span className="fin-pp__cell-muted">—</span>}
                                    {e && bilhetes.length === 0 && (
                                        <div className="d-flex align-items-center gap-2 flex-wrap">
                                            {e.situacao === 'cancelado' ? <span className="fin-pp__cell-muted">—</span> : <span className="fin-pp__pill is-warn">Sem bilhete</span>}
                                            {dblCheck}
                                        </div>
                                    )}
                                    {/* Os que valem; o DBL CHECK vai no primeiro deles. Os substituídos por um gravado depois
                                        na mesma linha ficam atrás do link. */}
                                    {valendo.map((b, n) => bilhete(b, semGravar, linhaCancelada, n === 0 ? dblCheck : null, operavel, minha, false,
                                        podeEditarColunas ? () => onColunas(alvo, 'dblcheck') : null))}
                                    {substituidos.size > 0 && (
                                        <div style={{ padding: '6px 0', borderTop: '1px solid var(--fin-bd)' }}>
                                            <button type="button" className="fin-pp__linkbtn" onClick={() => alternarAberto('substituidos:' + e.id)}>
                                                {verSubstituidos ? <FaChevronDown size={10} /> : <FaChevronRight size={10} />} {verSubstituidos ? 'Ocultar' : 'Mostrar'} substituídos ({substituidos.size})
                                            </button>
                                        </div>
                                    )}
                                    {verSubstituidos && bilhetes.filter(b => substituidos.has(b.id))
                                        .map(b => bilhete(b, semGravar, linhaCancelada, null, operavel, minha, true))}
                                </td>
                            </tr>
                        );
                    })}
                </tbody>
            </table>
            {/* Cancelar é status, como qualquer outro: o cancelamento escolhido e a descrição ficam no
                rascunho da linha, e vão no envio da tela. */}
            <ConfirmarAcaoModal aberto={!!cancelando} titulo={aereo ? 'Cancelar a emissão?' : 'Solicitar o cancelamento da emissão?'} subtitulo={cancelando?.linha?.nome}
                rotulo="Salvar" rotuloFechar="Voltar" tom="danger" icone="danger"
                // O RSVP tira o cancelamento que ainda não foi ao Aéreo: o da tela sai, e o salvo sai no Salvar.
                alternativa={cancelando?.temRascunho ? {
                    rotulo: 'Tirar o cancelamento', tom: 'danger-ghost',
                    onConfirmar: () => { onCancelar(cancelando.linha, null); setCancelando(null); },
                } : null}
                confirmarPreso={(aereo && !cancelando?.rotulo) || !String(cancelando?.descricao ?? '').trim()
                    || (pedeReembolso && (cancelando?.reembolso ?? null) === null)}
                onConfirmar={() => {
                    onCancelar(cancelando.linha, { rotulo: cancelando.rotulo, descricao: String(cancelando.descricao).trim(),
                        reembolso: pedeReembolso ? cancelando.reembolso : null });
                    setCancelando(null);
                }}
                onFechar={() => setCancelando(null)}>
                <div className="fin-pp__filters">
                    {/* Só o Aéreo escolhe o tipo do cancelamento — é o que o contador da planilha conta. O
                        RSVP descreve o que houve e a linha fica marcada "Cancelamento de emissão solicitado", com o status
                        que tinha; o tipo o Aéreo dá depois. */}
                    {aereo && (
                        <FinField label="Cancelamento" required full>
                            <ReactSelect options={opcoesCancelamento} placeholder="Escolha o cancelamento"
                                value={cancelando?.rotulo ? { value: cancelando.rotulo } : null}
                                formatOptionLabel={(o, meta) => meta?.context === 'menu' ? <PillLegenda rotulo={o.value} legenda={legenda} /> : o.label}
                                onChange={o => { const r = o ? o.value : ''; setCancelando(c => ({ ...c, rotulo: r })); }} />
                        </FinField>
                    )}
                    {pedeReembolso && (
                        <FinField label="Reembolso previsto" required full>
                            <FinNumber prefix="R$ " placeholder="R$ 0,00" value={cancelando?.reembolso ?? ''}
                                onValueChange={({ floatValue }) => setCancelando(c => ({ ...c, reembolso: floatValue ?? null }))} />
                        </FinField>
                    )}
                    <FinField label="Descrição" required full>
                        <FinTextarea rows={4} value={cancelando?.descricao ?? ''}
                            placeholder={aereo ? 'O que aconteceu, para o RSVP' : 'O que aconteceu, para o Aéreo'}
                            onChange={ev => { const descricao = ev.target.value; setCancelando(c => ({ ...c, descricao })); }} />
                    </FinField>
                </div>
                <p className="fin-pp__cell-muted" style={{ fontSize: 12, margin: '8px 0 0' }}>
                    {aereo ? 'Fica na tela até o Salvar e vai para o RSVP no Enviar para o RSVP, no rodapé.'
                        : 'A linha fica marcada como Cancelamento de emissão solicitado, sem mudar o status. Fica na tela até o Salvar e vai para o Aéreo no Solicitar Cancelamento de Emissão, no rodapé.'}
                </p>
            </ConfirmarAcaoModal>
            {/* O Reenviar do RSVP: a observação é obrigatória, e o rascunho fica na tela até o Solicitar
                Emissão de Aéreo. Com rascunho, o modal também o tira. */}
            <ConfirmarAcaoModal aberto={!!reenviando} titulo="Reenviar a linha ao Aéreo?" subtitulo={reenviando?.linha?.nome}
                rotulo="Salvar" rotuloFechar="Voltar" tom="primary" icone="enviar"
                confirmarPreso={!String(reenviando?.observacao ?? '').trim()}
                alternativa={reenviando?.temRascunho ? {
                    rotulo: 'Tirar o reenvio', tom: 'danger-ghost',
                    onConfirmar: () => { onReenviar(reenviando.linha, null); setReenviando(null); },
                } : null}
                onConfirmar={() => {
                    onReenviar(reenviando.linha, { observacao: String(reenviando.observacao).trim() });
                    setReenviando(null);
                }}
                onFechar={() => setReenviando(null)}>
                <div className="fin-pp__filters">
                    <FinField label="Observação" required full>
                        <FinTextarea rows={4} value={reenviando?.observacao ?? ''} placeholder="O que mudou, para o Aéreo"
                            onChange={ev => { const observacao = ev.target.value; setReenviando(r => ({ ...r, observacao })); }} />
                    </FinField>
                </div>
                <p className="fin-pp__cell-muted" style={{ fontSize: 12, margin: '8px 0 0' }}>
                    {'No Solicitar Emissão de Aéreo, no rodapé, a linha volta para EMITIR com esta observação'
                        + (reenviando?.linha?.emissao?.trocouEscolha ? ' e com o voo novo do convidado' : '')
                        + (reenviando?.linha?.emissao?.canceladoRsvp ? ', sem o pedido de cancelamento' : '')
                        + ', na mesma linha. Até lá o Salvar o guarda, sem mandar ao Aéreo.'}
                </p>
            </ConfirmarAcaoModal>
        </div>
    );
}
